/*
 * html-pdf.js — HtmlPdf: элемент страницы → ВЕКТОРНЫЙ PDF с настоящим текстом.
 *
 *   HtmlPdf.fromElement(el, opts) → Promise<Uint8Array>   (opts.as:'blob' → Blob)
 *   HtmlPdf.render(el, opts)      → Promise<{bytes, pages, warnings}>
 *   HtmlPdf.attach(pdfBytes, {name, bytes, mime, afRelationship, description}) → Promise<Uint8Array>
 *   HtmlPdf.fontCss() / HtmlPdf.installFonts() / HtmlPdf.FAMILY — шрифты экрана, совпадающие с файлом
 *
 * Раскладку делает сам браузер: каждое слово переносится в PDF туда, где его поставил браузер
 * (Range.getClientRects), а текст остаётся текстом — выделяется, копируется, ищется. Шрифты —
 * метрически совместимые Tinos/Arimo/Cousine/Carlito/Caladea (public/fonts/metric/ttf — те же
 * глифы, что woff2 на экране); письменности вне них (арабский, корейский, CJK, греческий…) —
 * запасные начертания PdfTextFont; знак без шрифта (эмодзи) — картинкой на своём месте.
 * Арабский и иврит — PdfTextFont.bidiRuns/runText (цифры и латиница внутри RTL встают как на
 * экране); слово запасным шрифтом вписывается в ширину, отведённую браузером (Tz).
 *
 * Ядро вынесено из /document-writer (export-pdf.js, 08.10.2026) и обобщено: фоны и рамки
 * любых блоков (со скруглением, пунктиром), линии таблиц, картинки (img, canvas; svg — вектором,
 * если он простой, иначе растром), ссылки (URI и внутренние #якоря), маркеры списков,
 * text-transform, letter-spacing, подчёркивание от предков, три режима страниц, колонтитулы,
 * PDF/A-3b (OutputIntent sRGB, XMP pdfaid, /ID) и вложения с AFRelationship (Factur-X/ZUGFeRD).
 *
 * Режимы страниц (opts.pages):
 *   'css'    — el содержит листы `.hp-page` (opts.pageSelector): каждый — страница PDF;
 *   'single' — el и есть один лист (высота — от элемента, если opts.page не задан: чек, этикетка);
 *   'auto'   — el — колонка текста шириной (лист − поля); примитив сам режет её на страницы
 *              между строками, не рвёт строки таблиц, break-inside:avoid, картинки; заголовок
 *              не остаётся последним на странице, абзац не оставляет одну строку (висячие);
 *              break-before/after:page — принудительный разрыв;
 *   'fixed'  — el — лента листов одинаковой высоты (opts.page) без промежутков (так /document-writer).
 *
 * Разбор, замеры и ограничения — docs/html-pdf.md.
 */
(function (root, factory) {
    'use strict';
    var api = factory(root);
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root && !root.HtmlPdf) root.HtmlPdf = api;
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : null), function (root) {
'use strict';

var PT = 0.75;                 // css px → pt
var MM = 96 / 25.4;            // мм → css px
var PAPER = { a4: [210, 297], a3: [297, 420], a5: [148, 210], a6: [105, 148], letter: [215.9, 279.4], legal: [215.9, 355.6] };
var VERSION = '1.0.0';

/* ── адреса ─────────────────────────────────────────────────────────────── */
var URLS = {
    pdfLib: ['/vendor/pdf-lib/pdf-lib.min.js'],
    fontkit: ['/vendor/fontkit/fontkit.umd.min.js'],
    pdfText: ['/js/pdf-text-font.js'],
    metricTtf: '/fonts/metric/ttf/',
    metricWoff2: '/fonts/metric/',
    fallbackFonts: '/vendor/fonts/'
};
function config(c) {
    c = c || {};
    var src = c.urls || c;
    Object.keys(URLS).forEach(function (k) { if (src[k] != null) URLS[k] = src[k]; });
    return URLS;
}
if (root && root.__HTML_PDF_URLS) config(root.__HTML_PDF_URLS);

/* ── чистые помощники (их проверяют узловые тесты) ───────────────────────── */

/* Семейство CSS → метрическое начертание PDF. Первое узнанное имя в стеке решает; общий
   sans-serif проверяется раньше serif (в «sans-serif» есть «serif»). */
function faceOf(fam) {
    var list = String(fam || '').split(',');
    for (var i = 0; i < list.length; i++) {
        var f = list[i].trim().replace(/^["']|["']$/g, '').toLowerCase();
        if (!f) continue;
        if (/carlito|calibri/.test(f)) return 'carlito';
        if (/caladea|cambria/.test(f)) return 'caladea';
        if (/cousine|courier|mono|consolas|menlo|monaco/.test(f)) return 'cousine';
        if (/arimo|arial|helvetica|liberation sans|verdana|tahoma|segoe|roboto|inter\b|system-ui|sans/.test(f)) return 'arimo';
        if (/tinos|times|liberation serif|georgia|garamond|serif/.test(f)) return 'tinos';
    }
    return 'tinos';
}
/* У каких начертаний есть файл курсива (у Cousine нет — курсив синтезируется наклоном, как в браузере). */
function fontFile(face, bold, italic) {
    var it = italic && face !== 'cousine';
    return face + '-' + (bold ? '700' : '400') + (it ? '-italic' : '');
}

function parseColor(c) {
    var s = String(c || '').trim();
    var m = s.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+)(%?))?\s*\)$/);
    if (m) {
        var a = m[4] == null ? 1 : (m[5] ? +m[4] / 100 : +m[4]);
        return { r: +m[1] / 255, g: +m[2] / 255, b: +m[3] / 255, a: a };
    }
    m = s.match(/^#([0-9a-f]{3,8})$/i);
    if (m) {
        var h = m[1];
        if (h.length <= 4) h = h.split('').map(function (x) { return x + x; }).join('');
        return { r: parseInt(h.substr(0, 2), 16) / 255, g: parseInt(h.substr(2, 2), 16) / 255, b: parseInt(h.substr(4, 2), 16) / 255, a: h.length >= 8 ? parseInt(h.substr(6, 2), 16) / 255 : 1 };
    }
    if (s === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
    return null;
}
/* Цвет, который не rgb() (oklch, color(), имя) — через холст: браузер сам переводит в sRGB. */
var colorCache = {};
function toRgb(c) {
    var p = parseColor(c);
    if (p) return p;
    if (colorCache[c]) return colorCache[c];
    var out = { r: 0, g: 0, b: 0, a: 1 };
    try {
        var cv = document.createElement('canvas'); cv.width = cv.height = 1;
        var x = cv.getContext('2d');
        x.fillStyle = '#000'; x.fillStyle = c; x.fillRect(0, 0, 1, 1);
        var d = x.getImageData(0, 0, 1, 1).data;
        out = { r: d[0] / 255, g: d[1] / 255, b: d[2] / 255, a: d[3] / 255 };
    } catch (e) {}
    colorCache[c] = out;
    return out;
}

function roman(n) {
    var v = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1], s = ['m', 'cm', 'd', 'cd', 'c', 'xc', 'l', 'xl', 'x', 'ix', 'v', 'iv', 'i'], out = '';
    for (var i = 0; i < v.length; i++) while (n >= v[i]) { out += s[i]; n -= v[i]; }
    return out;
}
/* Текст маркера пункта по list-style-type; 'disc'|'circle'|'square' — фигуры. */
function markerText(type, idx) {
    type = String(type || 'disc');
    var q = type.match(/^["'](.*)["']$/);
    if (q) return { text: q[1] };
    if (type === 'none') return null;
    if (type === 'disc' || type === 'circle' || type === 'square') return { shape: type };
    if (type === 'decimal-leading-zero') return { text: (idx < 10 && idx >= 0 ? '0' : '') + idx + '.' };
    if (/lower-alpha|lower-latin/.test(type)) return { text: String.fromCharCode(97 + ((idx - 1) % 26 + 26) % 26) + '.' };
    if (/upper-alpha|upper-latin/.test(type)) return { text: String.fromCharCode(65 + ((idx - 1) % 26 + 26) % 26) + '.' };
    if (/lower-roman/.test(type)) return { text: roman(idx) + '.' };
    if (/upper-roman/.test(type)) return { text: roman(idx).toUpperCase() + '.' };
    if (/lower-greek/.test(type)) return { text: String.fromCharCode(0x3b1 + ((idx - 1) % 24 + 24) % 24) + '.' };
    if (/cyrillic|russian/.test(type)) return { text: 'абвгдежзиклмнопрстуфхцчшщэюя'.charAt(((idx - 1) % 28 + 28) % 28) + ')' };
    return { text: idx + '.' };
}

/* Слова текстового узла. По одному знаку — только иероглифы и кана (между словами там нет
   пробелов); хангыль пишется словами. trail — за словом шёл пробел (в PDF он станет символом:
   текст копируется со словами врозь). */
var SEG_RE = /[\u3040-\u30ff\u3400-\u9fff\uf900-\ufaff]/;
function segments(text) {
    var out = [], re = /[^\s\u200b]+/g, m;
    while ((m = re.exec(text))) {
        var s = m[0], start = m.index, after = text.charAt(start + s.length);
        var trail = !!after && after !== '\u200b';
        if (!SEG_RE.test(s)) { out.push({ s: s, i: start, trail: trail }); continue; }
        var arr = Array.from(s), off = 0, prev = null;
        for (var i = 0; i < arr.length; i++) {
            var ch = arr[i];
            if (SEG_RE.test(ch) || !prev || SEG_RE.test(prev.s)) { prev = { s: ch, i: start + off, trail: false }; out.push(prev); }
            else prev.s += ch;
            off += ch.length;
        }
        prev.trail = trail;
    }
    return out;
}

/* text-transform на слово (DOM хранит исходный регистр, экран показывает преобразованный). */
function applyTransform(s, tt, lang) {
    if (!tt || tt === 'none') return s;
    try {
        if (tt === 'uppercase') return s.toLocaleUpperCase(lang || undefined);
        if (tt === 'lowercase') return s.toLocaleLowerCase(lang || undefined);
        if (tt === 'capitalize') return s.replace(/\p{L}/u, function (c) { return c.toLocaleUpperCase(lang || undefined); });
    } catch (e) {
        if (tt === 'uppercase') return s.toUpperCase();
        if (tt === 'lowercase') return s.toLowerCase();
    }
    return s;
}

/* Разбиение колонки на страницы — чистая функция.
   items: неразрывные отрезки {t, b, keepNext?, block?, line?, lines?}; forced: y принудительных
   разрывов; total: высота колонки; ch: высота текстового поля страницы. → [{y0, y1}]. */
function planBreaks(items, forced, total, ch) {
    var iv = items.filter(function (i) { return i.b > i.t; }).sort(function (a, b) { return a.t - b.t || a.b - b.b; });
    var merged = [];
    iv.forEach(function (i) {
        var m = merged[merged.length - 1];
        if (m && i.t < m.b - 0.01) { if (i.b > m.b) m.b = i.b; m.items.push(i); }
        else merged.push({ t: i.t, b: i.b, items: [i] });
    });
    forced = (forced || []).slice().sort(function (a, b) { return a - b; });
    var out = [], start = 0, guard = 0;
    if (!(ch > 1)) return [{ y0: 0, y1: total }];
    function idxBefore(y) { var k = -1; for (var i = 0; i < merged.length; i++) { if (merged[i].b <= y + 0.01) k = i; else break; } return k; }
    while (start < total - 0.5 && guard++ < 10000) {
        var limit = start + ch, cut = null, i;
        for (i = 0; i < forced.length; i++) if (forced[i] > start + 0.5 && forced[i] <= limit + 0.01) { cut = forced[i]; break; }
        if (cut != null) {
            // принудительный разрыв внутри строки — к её верху
            for (i = 0; i < merged.length; i++) if (merged[i].t < cut - 0.01 && merged[i].b > cut + 0.01) { if (merged[i].t > start + 0.5) cut = merged[i].t; break; }
        } else if (limit >= total - 0.01) {
            cut = total;
        } else {
            // первый отрезок, который не влез: режем по его верху (или по полю, если он ниже поля)
            var nx = -1;
            for (i = 0; i < merged.length; i++) if (merged[i].b > limit + 0.01) { nx = i; break; }
            cut = nx >= 0 && merged[nx].t < limit ? merged[nx].t : limit;
            if (cut <= start + 0.5) cut = limit;                 // выше страницы — режем по полю
            else {
                var min = start + ch * 0.35;                    // правила не опустошают страницу
                // висячие строки: одна первая строка абзаца внизу, одна последняя — наверху
                var at = nx >= 0 ? merged[nx].items[0] : null;
                var pk = idxBefore(cut), prev = pk >= 0 ? merged[pk] : null, pi = prev ? prev.items[0] : null;
                if (at && pi && at.block != null && at.block === pi.block && at.lines > 1) {
                    if (pi.line === 0 && prev.t > min) cut = prev.t;
                    else if (at.line === at.lines - 1 && pi.line === at.lines - 2 && at.lines >= 4 && prev.t > min) cut = prev.t;
                }
                // заголовок (и цепочка заголовков) уходит на следующую страницу вместе с текстом
                for (var hop = 0; hop < 4; hop++) {
                    pk = idxBefore(cut); prev = pk >= 0 ? merged[pk] : null;
                    if (!prev || !prev.items.some(function (x) { return x.keepNext; })) break;
                    var top = prev.t;
                    // заголовок в несколько строк — к его первой строке
                    var hb = prev.items[0].block;
                    for (var j = pk - 1; j >= 0 && hb != null && merged[j].items[0].block === hb; j--) top = merged[j].t;
                    if (top <= min) break;
                    cut = top;
                }
            }
        }
        out.push({ y0: start, y1: cut });
        start = cut;
    }
    if (!out.length) out.push({ y0: 0, y1: Math.max(total, 1) });
    return out;
}

/* Матрица SVG user space → страница PDF (вектор). m — DOMMatrix getScreenCTM (user → client),
   k — px→pt с учётом масштаба, (ox, oy) — начало координат клиента, H — высота страницы в pt,
   (sx, dy) — сдвиг среза страницы в px. pdf-lib drawSvgPath сам ставит scale(1,-1) — его
   компенсирует S, поэтому возвращается T' = S × T. */
function svgMatrix(m, k, ox, oy, H, srcX0, srcY0, dstY0, sc) {
    // клиент → px листа: x' = (x - ox)/sc - srcX0; y' = dstY0 + (y - oy)/sc - srcY0
    // px листа → pt PDF: X = x'·PT, Y = H - y'·PT
    var a = m.a, b = m.b, c = m.c, d = m.d, e = m.e, f = m.f;
    var kx = PT / sc;
    // T (row-vector): [a b; c d; e f] × P, P = [[kx,0],[0,-kx],[tx,ty]]
    var tx = (-ox / sc - srcX0) * PT, ty = H - (dstY0 - oy / sc - srcY0) * PT;
    var A = a * kx, B = -b * kx, C = c * kx, D = -d * kx, E = e * kx + tx, F = -f * kx + ty;
    // S × T: вторая строка меняет знак
    return [A, B, -C, -D, E, F];
}

/* XMP для PDF/A: значения ОБЯЗАНЫ совпадать с Info (veraPDF 6.6.2.3.1). */
function xmlEsc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
function isoDate(d) {
    function p(n) { return (n < 10 ? '0' : '') + n; }
    return d.getUTCFullYear() + '-' + p(d.getUTCMonth() + 1) + '-' + p(d.getUTCDate()) + 'T' + p(d.getUTCHours()) + ':' + p(d.getUTCMinutes()) + ':' + p(d.getUTCSeconds()) + 'Z';
}
function buildXmp(meta, part, conformance, extra) {
    var x = [];
    x.push('<?xpacket begin="\ufeff" id="W5M0MpCehiHzreSzNTczkc9d"?>');
    x.push('<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">');
    x.push('<rdf:Description rdf:about="" xmlns:pdfaid="http://www.aiim.org/pdfa/ns/id/"><pdfaid:part>' + part + '</pdfaid:part><pdfaid:conformance>' + conformance + '</pdfaid:conformance></rdf:Description>');
    var dc = '<dc:format>application/pdf</dc:format>';
    if (meta.title != null) dc += '<dc:title><rdf:Alt><rdf:li xml:lang="x-default">' + xmlEsc(meta.title) + '</rdf:li></rdf:Alt></dc:title>';
    if (meta.author != null) dc += '<dc:creator><rdf:Seq><rdf:li>' + xmlEsc(meta.author) + '</rdf:li></rdf:Seq></dc:creator>';
    if (meta.subject != null) dc += '<dc:description><rdf:Alt><rdf:li xml:lang="x-default">' + xmlEsc(meta.subject) + '</rdf:li></rdf:Alt></dc:description>';
    if (meta.lang) dc += '<dc:language><rdf:Bag><rdf:li>' + xmlEsc(meta.lang) + '</rdf:li></rdf:Bag></dc:language>';
    x.push('<rdf:Description rdf:about="" xmlns:dc="http://purl.org/dc/elements/1.1/">' + dc + '</rdf:Description>');
    var xmp = '<xmp:CreateDate>' + isoDate(meta.created) + '</xmp:CreateDate><xmp:ModifyDate>' + isoDate(meta.modified) + '</xmp:ModifyDate><xmp:MetadataDate>' + isoDate(meta.modified) + '</xmp:MetadataDate>';
    if (meta.creator != null) xmp += '<xmp:CreatorTool>' + xmlEsc(meta.creator) + '</xmp:CreatorTool>';
    x.push('<rdf:Description rdf:about="" xmlns:xmp="http://ns.adobe.com/xap/1.0/">' + xmp + '</rdf:Description>');
    var pdfns = '';
    if (meta.producer != null) pdfns += '<pdf:Producer>' + xmlEsc(meta.producer) + '</pdf:Producer>';
    if (meta.keywords != null) pdfns += '<pdf:Keywords>' + xmlEsc(meta.keywords) + '</pdf:Keywords>';
    if (pdfns) x.push('<rdf:Description rdf:about="" xmlns:pdf="http://ns.adobe.com/pdf/1.3/">' + pdfns + '</rdf:Description>');
    if (extra) x.push(String(extra));
    x.push('</rdf:RDF></x:xmpmeta>');
    // запас для правки на месте — как у Acrobat
    var pad = ''; for (var i = 0; i < 20; i++) pad += '                                                                                                    \n';
    x.push(pad + '<?xpacket end="w"?>');
    return x.join('\n');
}
/* Расширение XMP Factur-X / ZUGFeRD 2.x (схема описана, как требует PDF/A — pdfaExtension). */
function facturXXmp(o) {
    o = o || {};
    var ns = 'urn:factur-x:pdfa:CrossIndustryDocument:invoice:1p0#';
    function prop(name, desc) {
        return '<rdf:li rdf:parseType="Resource"><pdfaProperty:name>' + name + '</pdfaProperty:name><pdfaProperty:valueType>Text</pdfaProperty:valueType><pdfaProperty:category>external</pdfaProperty:category><pdfaProperty:description>' + desc + '</pdfaProperty:description></rdf:li>';
    }
    return '<rdf:Description rdf:about="" xmlns:pdfaExtension="http://www.aiim.org/pdfa/ns/extension/" xmlns:pdfaSchema="http://www.aiim.org/pdfa/ns/schema#" xmlns:pdfaProperty="http://www.aiim.org/pdfa/ns/property#">' +
        '<pdfaExtension:schemas><rdf:Bag><rdf:li rdf:parseType="Resource">' +
        '<pdfaSchema:schema>Factur-X PDFA Extension Schema</pdfaSchema:schema>' +
        '<pdfaSchema:namespaceURI>' + ns + '</pdfaSchema:namespaceURI><pdfaSchema:prefix>fx</pdfaSchema:prefix>' +
        '<pdfaSchema:property><rdf:Seq>' +
        prop('DocumentFileName', 'name of the embedded XML invoice file') +
        prop('DocumentType', 'INVOICE') +
        prop('Version', 'The actual version of the Factur-X XML schema') +
        prop('ConformanceLevel', 'The conformance level of the embedded Factur-X data') +
        '</rdf:Seq></pdfaSchema:property></rdf:li></rdf:Bag></pdfaExtension:schemas></rdf:Description>' +
        '<rdf:Description rdf:about="" xmlns:fx="' + ns + '">' +
        '<fx:DocumentType>' + xmlEsc(o.documentType || 'INVOICE') + '</fx:DocumentType>' +
        '<fx:DocumentFileName>' + xmlEsc(o.documentFileName || 'factur-x.xml') + '</fx:DocumentFileName>' +
        '<fx:Version>' + xmlEsc(o.version || '1.0') + '</fx:Version>' +
        '<fx:ConformanceLevel>' + xmlEsc(o.conformanceLevel || 'EN 16931') + '</fx:ConformanceLevel>' +
        '</rdf:Description>';
}

/* sRGB IEC61966-2.1, v2 «magic» из saucecontrol/Compact-ICC-Profiles (CC0 1.0), 736 байт. */
var SRGB_ICC_B64 = 'AAAC4GxjbXMCEAAAbW50clJHQiBYWVogB+IAAwAUAAkADgAdYWNzcE1TRlQAAAAAc2F3c2N0cmwAAAAAAAAAAAAAAAAAAPbWAAEAAAAA0y1oYW5kk7I0qQ6wIoqY/Zqvo2eJmwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJZGVzYwAAAPAAAABfY3BydAAAAQwAAAAMd3RwdAAAARgAAAAUclhZWgAAASwAAAAUZ1hZWgAAAUAAAAAUYlhZWgAAAVQAAAAUclRSQwAAAWgAAAF4Z1RSQwAAAWgAAAF4YlRSQwAAAWgAAAF4ZGVzYwAAAAAAAAAFc1JHQgAAAAAAAAAAAAAAAHRleHQAAAAAQ0MwAFhZWiAAAAAAAADzVAABAAAAARbJWFlaIAAAAAAAAG+gAAA48gAAA49YWVogAAAAAAAAYpYAALeJAAAY2lhZWiAAAAAAAAAkoAAAD4UAALbEY3VydgAAAAAAAAC2AAAAHAA4AFQAcACMAKgAxADhAQABIgFGAW0BlQHBAfACIAJVAosCxAMBAz8DggPGBA4EWQSnBPkFTAWkBf4GXAa+ByEHigf0CGMI1QlJCcMKPwq/C0ILyQxUDOENdA4JDqIPQA/gEIURLRHaEooTPhP2FLIVcRY2Fv0XyhiZGW4aRhsiHAMc5x3QHr0friCkIZ4inCOfJKUlsSbAJ9Uo7SoKKyssUS18Lqov3jEWMlIzlDTZNiQ3czjGOiA7fDzfPkU/sEEhQpZEEEWPRxJIm0ooS7tNUU7uUI9SNVPgVZBXRVkAWr5chF5MYBth72PHZaZniWlxa19tUW9KcUZzSnVRd155cXuIfaZ/yIHwhB6GUIiJisWNCY9RkZ+T85ZLmKubDp14n+eiW6TWp1ap26xnrvexj7Qqtsy5dLwhvtXBjcRMxxDJ2syrz3/SXNU92CTbEt4E4P7j/OcB6gztHPA081D2c/mb/Mr//w==';
function b64bytes(b64) {
    if (typeof atob === 'function') { var s = atob(b64), u = new Uint8Array(s.length); for (var i = 0; i < s.length; i++) u[i] = s.charCodeAt(i); return u; }
    return new Uint8Array(Buffer.from(b64, 'base64'));
}

/* Размер листа в мм. */
function pageMm(page, orientation) {
    var w, h;
    if (page && typeof page === 'object') { w = +page.wMm; h = +page.hMm; }
    else { var p = PAPER[String(page || 'a4').toLowerCase()] || PAPER.a4; w = p[0]; h = p[1]; }
    if (orientation === 'landscape' && h > w) { var t = w; w = h; h = t; }
    return { w: w, h: h };
}
function margins(m) {
    if (m == null) return { t: 20, r: 15, b: 20, l: 20 };
    if (typeof m === 'number') return { t: m, r: m, b: m, l: m };
    if (Array.isArray(m)) return { t: +m[0] || 0, r: +(m[1] != null ? m[1] : m[0]) || 0, b: +(m[2] != null ? m[2] : m[0]) || 0, l: +(m[3] != null ? m[3] : (m[1] != null ? m[1] : m[0])) || 0 };
    return { t: +m.t || 0, r: +m.r || 0, b: +m.b || 0, l: +m.l || 0 };
}

var PURE = {
    faceOf: faceOf, fontFile: fontFile, parseColor: parseColor, markerText: markerText, segments: segments,
    applyTransform: applyTransform, planBreaks: planBreaks, svgMatrix: svgMatrix, buildXmp: buildXmp,
    facturXXmp: facturXXmp, pageMm: pageMm, margins: margins, srgbIcc: function () { return b64bytes(SRGB_ICC_B64); }
};
if (!root || typeof document === 'undefined') return { VERSION: VERSION, _pure: PURE, config: config };

/* ════════════════════════ браузерная часть ════════════════════════════ */

var RTL_RE = /[\u0590-\u08ff\ufb1d-\ufdff\ufe70-\ufeff]/;
var ARABIC_RE = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb50-\ufdff\ufe70-\ufefc]/;
var NUM_AN = /[\u0660-\u0669\u06f0-\u06f9]/;
/* Картинкой — только знаки-рисунки (эмодзи, ✓, ☎): запасной шрифт письма рисовал их чужими
   знаками (✓ выходил «√», замер DW 08.10). Буквы, цифры, валюта (₽), пунктуация — шрифтом:
   они остаются текстом и копируются. */
var PICTO_RE = /^[\p{So}\p{Extended_Pictographic}\p{Sk}\p{M}\u200d\ufe0e\ufe0f\s]+$/u;

function bounded(p, ms, phase) {
    if (root.__bounded) return root.__bounded(p, ms, phase);
    return new Promise(function (res, rej) {
        var t = setTimeout(function () { var e = new Error('wait timeout: ' + phase); e.code = 'WAIT_TIMEOUT'; e.phase = phase; rej(e); }, ms);
        Promise.resolve(p).then(function (v) { clearTimeout(t); res(v); }, function (e) { clearTimeout(t); rej(e); });
    });
}
function abortErr() { try { return new DOMException('aborted', 'AbortError'); } catch (e) { var x = new Error('aborted'); x.name = 'AbortError'; return x; } }
function checkAbort(sig) { if (sig && sig.aborted) throw abortErr(); }
function typed(code, msg) { var e = new Error(msg || code); e.code = code; return e; }

/* ── загрузка библиотек ─────────────────────────────────────────────────── */
var scriptJobs = {};
function loadScript(list, expect, label) {
    if (root[expect]) return Promise.resolve();
    list = [].concat(list || []).filter(Boolean);
    var key = list.join('|');
    if (scriptJobs[key]) return scriptJobs[key];
    var job;
    if (typeof root.__vendorScript === 'function') {
        job = root.__vendorScript(list, { expect: expect, label: label });
    } else {
        job = new Promise(function (resolve, reject) {
            var i = 0, tries = [];
            list.forEach(function (u) { tries.push(u, u + (u.indexOf('?') < 0 ? '?' : '&') + 'r=1'); });
            (function next() {
                if (root[expect]) { resolve(); return; }
                if (i >= tries.length) { reject(typed('HP_LIBS', 'html-pdf: cannot load ' + label)); return; }
                var s = document.createElement('script'), done = false;
                s.src = tries[i++]; s.async = true;
                var n = document.querySelector('script[nonce]'); if (n && n.nonce) s.nonce = n.nonce;
                var g = setTimeout(function () { if (!done) { done = true; s.remove(); next(); } }, 45000);
                s.onload = function () { if (done) return; done = true; clearTimeout(g); if (root[expect]) resolve(); else next(); };
                s.onerror = function () { if (done) return; done = true; clearTimeout(g); s.remove(); next(); };
                document.head.appendChild(s);
            })();
        });
    }
    scriptJobs[key] = job.then(function () { if (!root[expect]) throw typed('HP_LIBS', 'html-pdf: ' + label + ' missing'); });
    scriptJobs[key].catch(function () { delete scriptJobs[key]; });
    return scriptJobs[key];
}
function load() {
    return Promise.all([
        loadScript(URLS.pdfLib, 'PDFLib', 'pdf-lib'),
        loadScript(URLS.fontkit, 'fontkit', 'fontkit')
    ]).then(function () { return loadScript(URLS.pdfText, 'PdfTextFont', 'pdf-text-font'); })
      .then(function () { return root.PdfTextFont.load().catch(function () { return null; }); });
}

/* ── шрифты экрана ───────────────────────────────────────────────────────
   Те же глифы на экране и в файле — и браузер ставит слова ровно туда, где их нарисует PDF.
   Стек FAMILY: метрическое начертание, затем запасные письменностей (по unicode-range — файл
   качается, только если такие знаки есть на странице). */
var LAT_RANGE = 'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+2074,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
var CYR_RANGE = 'U+0301,U+0400-045F,U+0490-0491,U+04B0-04B1,U+2116';
var METRIC = {
    tinos: { css: 'HP Tinos', local: ['Tinos', 'Liberation Serif'] },
    arimo: { css: 'HP Arimo', local: ['Arimo', 'Liberation Sans'] },
    cousine: { css: 'HP Cousine', local: ['Cousine', 'Liberation Mono'], noItalic: true },
    carlito: { css: 'HP Carlito', local: ['Carlito'] },
    caladea: { css: 'HP Caladea', local: ['Caladea'], noCyr: true }
};
var SCRIPT_FACES = [
    // латиница-расширенная (\u015f \u0142 \u0130), греческий, кириллица-расширенная, валюты (₽ ₴ ₸) — у
    // метрических начертаний их нет, PDF берёт Noto Sans; экран — тот же файл
    { css: 'HP Noto', file: 'NotoSans', range: 'U+0100-024F,U+0259,U+0370-03FF,U+0460-052F,U+1E00-1EFF,U+20A0-20AB,U+20AD-20CF,U+2100-2115,U+2117-214F' },
    { css: 'HP Arabic', file: 'IBMPlexSansArabic', range: 'U+0600-06FF,U+0750-077F,U+08A0-08FF,U+FB50-FDFF,U+FE70-FEFF' },
    { css: 'HP Korean', file: 'NotoSansKR', range: 'U+1100-11FF,U+3130-318F,U+A960-A97F,U+AC00-D7AF,U+D7B0-D7FF' },
    { css: 'HP CJK', file: 'NotoSansSC', range: 'U+2E80-2FDF,U+3000-303F,U+3400-4DBF,U+4E00-9FFF,U+F900-FAFF,U+FF00-FFEF' },
    { css: 'HP Japanese', file: 'NotoSansJP', range: 'U+3040-30FF,U+31F0-31FF' }
];
var SCRIPT_STACK = SCRIPT_FACES.map(function (f) { return "'" + f.css + "'"; }).join(',');
var FAMILY = {
    serif: "'HP Tinos'," + SCRIPT_STACK + ',serif',
    sans: "'HP Arimo'," + SCRIPT_STACK + ',sans-serif',
    mono: "'HP Cousine'," + SCRIPT_STACK + ',monospace',
    calibri: "'HP Carlito'," + SCRIPT_STACK + ',sans-serif',
    cambria: "'HP Caladea','HP Tinos'," + SCRIPT_STACK + ',serif'
};
/* ── зарегистрированные семейства ────────────────────────────────────────
   Страница, которая сама ставит свои шрифты (редактор презентаций), называет семейство экрана
   и TTF того же начертания: registerFamily('PM Montserrat', {400: url, 700: url, '400i': url}).
   Такое семейство в стеке font-family решает раньше метрических (faceOf сводил бы его к
   Tinos/Arimo, и строка в PDF выходила бы другой ширины). Нет курсивного файла — слово
   рисуется прямым, если не сказано synthItalic: экран с font-synthesis:none тоже не
   наклоняет. */
var FAMILIES = {};
function registerFamily(css, files, opts) {
    var key = String(css || '').trim().replace(/^["']|["']$/g, '').toLowerCase();
    if (!key || !files) return;
    FAMILIES[key] = { files: files, synthItalic: !!(opts && opts.synthItalic) };
}
function regFaceOf(fam) {
    var list = String(fam || '').split(',');
    for (var i = 0; i < list.length; i++) {
        var f = list[i].trim().replace(/^["']|["']$/g, '').toLowerCase();
        if (FAMILIES[f]) return 'reg:' + f;
        if (f && !/^hp /.test(f)) return null;          // первым стоит чужое семейство — решает faceOf
    }
    return null;
}
/* Файл начертания: ключ (он же имя шрифта в PDF) и адрес; у зарегистрированного — ближайший вес. */
function faceFile(face, bold, italic, wt) {
    if (face && face.indexOf('reg:') === 0) {
        var reg = FAMILIES[face.slice(4)], files = reg.files;
        var want = wt || (bold ? 700 : 400), best = null;
        Object.keys(files).forEach(function (k) {
            var w = parseInt(k, 10), it = /i$/.test(k);
            if (it !== !!italic) return;
            if (best == null || Math.abs(w - want) < Math.abs(best - want)) best = w;
        });
        var hasItalic = best != null;
        if (best == null) Object.keys(files).forEach(function (k) { var w = parseInt(k, 10); if (!/i$/.test(k) && (best == null || Math.abs(w - want) < Math.abs(best - want))) best = w; });
        var fk = best + (italic && hasItalic ? 'i' : '');
        return { key: face.slice(4).replace(/[^a-z0-9]/g, '') + '-' + fk, url: files[fk], skew: italic && !hasItalic && reg.synthItalic ? 0.25 : 0 };
    }
    var k2 = fontFile(face, bold, italic);
    return { key: k2, url: URLS.metricTtf + k2 + '.ttf', skew: italic && face === 'cousine' ? 0.25 : 0 };
}

function fontCss() {
    var out = [], base = URLS.metricWoff2;
    Object.keys(METRIC).forEach(function (face) {
        var m = METRIC[face];
        [[400, 'normal'], [700, 'normal'], [400, 'italic'], [700, 'italic']].forEach(function (v) {
            if (v[1] === 'italic' && m.noItalic) return;
            var suf = v[0] + (v[1] === 'italic' ? '-italic' : '');
            var loc = m.local.map(function (n) { return "local('" + n + (v[0] === 700 ? ' Bold' : '') + (v[1] === 'italic' ? ' Italic' : '') + "')"; }).join(',');
            [['latin', LAT_RANGE]].concat(m.noCyr ? [] : [['cyrillic', CYR_RANGE]]).forEach(function (sub) {
                out.push("@font-face{font-family:'" + m.css + "';src:" + loc + ',url(' + base + face + '-' + sub[0] + '-' + suf + ".woff2) format('woff2');font-weight:" + v[0] + ';font-style:' + v[1] + ';font-display:swap;unicode-range:' + sub[1] + '}');
            });
        });
    });
    SCRIPT_FACES.forEach(function (f) {
        [400, 700].forEach(function (w) {
            out.push("@font-face{font-family:'" + f.css + "';src:url(" + URLS.fallbackFonts + f.file + '-' + (w === 700 ? 'Bold' : 'Regular') + ".ttf) format('truetype');font-weight:" + w + ';font-display:swap;unicode-range:' + f.range + '}');
        });
    });
    return out.join('\n');
}
function installFonts() {
    if (!document.getElementById('hp-fonts')) {
        var st = document.createElement('style');
        st.id = 'hp-fonts';
        var n = document.querySelector('style[nonce],script[nonce]'); if (n && n.nonce) st.nonce = n.nonce;
        st.textContent = fontCss();
        document.head.appendChild(st);
    }
    return document.fonts && document.fonts.ready ? bounded(document.fonts.ready, 8000, 'fonts_ready').catch(function () {}) : Promise.resolve();
}

/* ── байты ───────────────────────────────────────────────────────────── */
var bytesCache = {};
function fetchBytes(url, phase) {
    if (!bytesCache[url]) {
        var job = fetch(url, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw typed('HP_FONT', 'font ' + r.status + ' ' + url); return r.arrayBuffer(); })
            .then(function (b) { var u = new Uint8Array(b); try { return root.PdfTextFont && root.PdfTextFont.alignGlyphData ? root.PdfTextFont.alignGlyphData(u) : u; } catch (e) { return u; } });
        bytesCache[url] = bounded(job, 45000, phase || 'html_pdf_font').catch(function (e) { delete bytesCache[url]; throw e; });
    }
    return bytesCache[url];
}

/* ── замер ──────────────────────────────────────────────────────────────── */
var blCache = {};
function baselineOffset(cs) {
    var key = cs.fontFamily + '|' + cs.fontSize + '|' + cs.fontWeight + '|' + cs.fontStyle;
    if (blCache[key] != null) return blCache[key];
    var probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;white-space:nowrap;line-height:normal;visibility:hidden;font-family:' + cs.fontFamily + ';font-size:' + cs.fontSize + ';font-weight:' + cs.fontWeight + ';font-style:' + cs.fontStyle;
    var span = document.createElement('span'); span.textContent = 'Hxgя';
    var mark = document.createElement('span'); mark.style.cssText = 'display:inline-block;width:1px;height:0;vertical-align:baseline';
    probe.appendChild(span); probe.appendChild(mark);
    document.body.appendChild(probe);
    var v = mark.getBoundingClientRect().bottom - span.getBoundingClientRect().top;
    probe.remove();
    blCache[key] = v;
    return v;
}
function px(v) { return parseFloat(v) || 0; }
function radiusOf(v) { return px(String(v || '0').split(' ')[0]); }
function isBlock(cs) { return !/^inline/.test(cs.display) && cs.display !== 'contents' && cs.display !== 'ruby'; }
function visibleColor(c) { var p = toRgb(c); return p.a > 0.003 ? p : null; }

function measure(el, o, warn) {
    var er = el.getBoundingClientRect();
    if (!(er.width > 0) || !(er.height > 0)) throw typed('HP_NOT_LAID_OUT', 'html-pdf: element has no box (display:none or detached)');
    /* Масштаб (превью в transform: scale) — отношение коробки к раскладке. ⚠️ offsetWidth —
       ЦЕЛОЕ: лист 210 мм = 793,7 px, а offsetWidth 794, и «масштаб» 0,99962 растягивал все
       координаты — на пятой странице ленты /document-writer слова уезжали на 2 px (0,5 мм,
       замер стенда 21.10). Разница в пределах округления — не масштаб. */
    var sc = el.offsetWidth ? er.width / el.offsetWidth : 1;
    if (!(sc > 0.01) || Math.abs(er.width - el.offsetWidth) <= 1) sc = 1;
    var ox = er.left, oy = er.top;
    function R(r) { return { x: (r.left - ox) / sc, y: (r.top - oy) / sc, w: r.width / sc, h: r.height / sc }; }
    var lang = o.lang || (el.closest && el.closest('[lang]') !== document.documentElement && el.closest('[lang]') ? el.closest('[lang]').getAttribute('lang') : '') || root.__LOCALE || document.documentElement.lang || '';
    var ops = [], items = [], forced = [], styles = new Map(), blocks = new Map(), blockIds = 0;
    var range = document.createRange();
    var pseudoWarned = false;

    function st(e) {
        var s = styles.get(e);
        if (s) return s;
        var cs = getComputedStyle(e);
        s = {
            cs: cs, face: regFaceOf(cs.fontFamily) || faceOf(cs.fontFamily), wt: parseInt(cs.fontWeight, 10) || 400, bold: (parseInt(cs.fontWeight, 10) || 400) >= 600,
            italic: cs.fontStyle === 'italic' || /^oblique/.test(cs.fontStyle), sizePx: px(cs.fontSize),
            color: toRgb(cs.color), ls: cs.letterSpacing === 'normal' ? 0 : px(cs.letterSpacing),
            tt: cs.textTransform, hyph: cs.hyphens, rtl: cs.direction === 'rtl', visible: cs.visibility === 'visible'
        };
        styles.set(e, s);
        return s;
    }
    function blockOf(e) {
        for (var x = e; x && x !== el.parentNode; x = x.parentElement) {
            if (blocks.has(x)) return blocks.get(x);
            if (x === el || isBlock(st(x).cs)) {
                var b = { id: blockIds++, el: x, keepNext: /^H[1-6]$/.test(x.tagName) || st(x).cs.breakAfter === 'avoid' || st(x).cs.breakAfter === 'avoid-page', lines: [] };
                blocks.set(x, b);
                return b;
            }
        }
        return null;
    }
    // подчёркивание и зачёркивание не наследуются, а РАСПРОСТРАНЯЮТСЯ от предков
    var decoCache = new Map();
    function decoOf(e) {
        if (decoCache.has(e)) return decoCache.get(e);
        var lines = [], color = null;
        for (var x = e; x && x !== el.parentNode; x = x.parentElement) {
            var cs = st(x).cs, d = cs.textDecorationLine || cs.textDecoration || '';
            if (/underline|line-through|overline/.test(d)) {
                d.split(/\s+/).forEach(function (t) { if (/underline|line-through|overline/.test(t) && lines.indexOf(t) < 0) lines.push(t); });
                if (!color) color = visibleColor(cs.textDecorationColor || cs.color) || toRgb(cs.color);
            }
            if (isBlock(cs) || x === el) break;
        }
        var r = lines.length ? { lines: lines, color: color } : null;
        decoCache.set(e, r);
        return r;
    }

    function pushWord(node, par, s, sg, rects) {
        var r = rects;
        var text = applyTransform(sg.s, s.tt, lang).replace(/[\u00ad\u200b\u2060\ufeff\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '');
        if (!text) return;
        ops.push({ t: 'text', s: text, r: r, st: s, bl: baselineOffset(s.cs), el: par, trail: sg.trail, deco: decoOf(par), blk: blockOf(par), al: s.rtl && ARABIC_RE.test(node.nodeValue) });
    }
    function lineGroups(rs) {
        var groups = [];
        for (var i = 0; i < rs.length; i++) {
            var r = rs[i];
            if (!(r.width > 0.01) && !(r.height > 0.01)) continue;
            var g = groups[groups.length - 1];
            if (g && Math.min(g.bottom, r.bottom) - Math.max(g.top, r.top) > 0.5 * Math.min(g.bottom - g.top, r.height)) {
                g.left = Math.min(g.left, r.left); g.right = Math.max(g.right, r.right); g.top = Math.min(g.top, r.top); g.bottom = Math.max(g.bottom, r.bottom);
            } else groups.push({ left: r.left, right: r.right, top: r.top, bottom: r.bottom });
        }
        return groups.map(function (g) { return { left: g.left, top: g.top, width: g.right - g.left, height: g.bottom - g.top }; });
    }
    function textNode(n) {
        var v = n.nodeValue;
        if (!v || !/\S/.test(v.replace(/[\u200b\u00ad]/g, ''))) return;
        var par = n.parentNode, s = st(par);
        if (!s.visible || s.color.a <= 0.003) return;
        segments(v).forEach(function (sg) {
            range.setStart(n, sg.i); range.setEnd(n, sg.i + sg.s.length);
            var g = lineGroups(range.getClientRects());
            if (!g.length) return;
            if (g.length === 1) { pushWord(n, par, s, sg, R(g[0])); return; }
            // слово разорвано переносом строки — по частям строк (форма букв внутри части сохраняется)
            var chars = Array.from(sg.s), off = 0, parts = [], cur = null;
            for (var k = 0; k < chars.length; k++) {
                var ch = chars[k];
                range.setStart(n, sg.i + off); range.setEnd(n, sg.i + off + ch.length);
                var cr = lineGroups(range.getClientRects())[0];
                if (cr) {
                    if (cur && Math.abs(cr.top - cur.top) < cr.height * 0.5) { cur.s += ch; cur.end = off + ch.length; }
                    else { cur = { s: ch, start: off, end: off + ch.length, top: cr.top }; parts.push(cur); }
                } else if (cur) { cur.s += ch; cur.end = off + ch.length; }
                off += ch.length;
            }
            parts.forEach(function (p, pi) {
                range.setStart(n, sg.i + p.start); range.setEnd(n, sg.i + p.end);
                var pr = lineGroups(range.getClientRects())[0];
                if (!pr) return;
                var last = pi === parts.length - 1, txt = p.s;
                // перенос по слогам: браузер рисует дефис, которого нет в DOM
                if (!last && (/\u00ad$/.test(txt) || (s.hyph === 'auto' && !/[-‐‑]$/.test(txt)))) txt = txt.replace(/\u00ad$/, '') + '-';
                pushWord(n, par, s, { s: txt, trail: last ? sg.trail : false }, R(pr));
            });
        });
    }

    function boxOps(e, cs, r, inline, rectsList) {
        var bg = visibleColor(cs.backgroundColor);
        var sides = ['Top', 'Right', 'Bottom', 'Left'].map(function (sd) {
            var w = px(cs['border' + sd + 'Width']), style = cs['border' + sd + 'Style'], c = visibleColor(cs['border' + sd + 'Color']);
            return (w > 0 && style !== 'none' && style !== 'hidden' && c) ? { w: w, style: style, c: c } : null;
        });
        if (cs.backgroundImage && cs.backgroundImage !== 'none') warn('background_image_skipped');
        if (!bg && !sides.some(Boolean)) return;
        var rad = [radiusOf(cs.borderTopLeftRadius), radiusOf(cs.borderTopRightRadius), radiusOf(cs.borderBottomRightRadius), radiusOf(cs.borderBottomLeftRadius)];
        var collapse = (e.tagName === 'TD' || e.tagName === 'TH' || e.tagName === 'TABLE') && cs.borderCollapse === 'collapse';
        if (inline) {
            rectsList.forEach(function (rr, i) {
                var sd = sides.slice();
                if (i > 0) sd[3] = null;                        // левая граница — у первого куска
                if (i < rectsList.length - 1) sd[1] = null;     // правая — у последнего
                ops.push({ t: 'box', el: e, r: rr, bg: bg, sides: sd, rad: [0, 0, 0, 0], collapse: false });
            });
        } else ops.push({ t: 'box', el: e, r: r, bg: bg, sides: sides, rad: rad, collapse: collapse });
    }

    function shapePath(n) {
        var tg = n.tagName.toLowerCase();
        function num(a) { try { return n[a].baseVal.value; } catch (e) { return parseFloat(n.getAttribute(a)) || 0; } }
        function f(v) { return Math.round(v * 1000) / 1000; }
        if (tg === 'path') return n.getAttribute('d') || '';
        if (tg === 'rect') {
            var x = num('x'), y = num('y'), w = num('width'), h = num('height');
            if (!(w > 0 && h > 0)) return '';
            var hasRx = n.hasAttribute('rx'), hasRy = n.hasAttribute('ry');
            var rx = hasRx ? num('rx') : 0, ry = hasRy ? num('ry') : 0;
            if (hasRx && !hasRy) ry = rx; if (hasRy && !hasRx) rx = ry;
            rx = Math.min(rx, w / 2); ry = Math.min(ry, h / 2);
            if (!(rx > 0 && ry > 0)) return 'M' + f(x) + ' ' + f(y) + 'H' + f(x + w) + 'V' + f(y + h) + 'H' + f(x) + 'Z';
            return 'M' + f(x + rx) + ' ' + f(y) + 'H' + f(x + w - rx) + 'A' + f(rx) + ' ' + f(ry) + ' 0 0 1 ' + f(x + w) + ' ' + f(y + ry) +
                'V' + f(y + h - ry) + 'A' + f(rx) + ' ' + f(ry) + ' 0 0 1 ' + f(x + w - rx) + ' ' + f(y + h) +
                'H' + f(x + rx) + 'A' + f(rx) + ' ' + f(ry) + ' 0 0 1 ' + f(x) + ' ' + f(y + h - ry) +
                'V' + f(y + ry) + 'A' + f(rx) + ' ' + f(ry) + ' 0 0 1 ' + f(x + rx) + ' ' + f(y) + 'Z';
        }
        if (tg === 'circle' || tg === 'ellipse') {
            var cx = num('cx'), cy = num('cy'), erx = tg === 'circle' ? num('r') : num('rx'), ery = tg === 'circle' ? erx : num('ry');
            if (!(erx > 0 && ery > 0)) return '';
            return 'M' + f(cx - erx) + ' ' + f(cy) + 'A' + f(erx) + ' ' + f(ery) + ' 0 1 0 ' + f(cx + erx) + ' ' + f(cy) + 'A' + f(erx) + ' ' + f(ery) + ' 0 1 0 ' + f(cx - erx) + ' ' + f(cy) + 'Z';
        }
        if (tg === 'line') return 'M' + f(num('x1')) + ' ' + f(num('y1')) + 'L' + f(num('x2')) + ' ' + f(num('y2'));
        if (tg === 'polyline' || tg === 'polygon') {
            var pts = String(n.getAttribute('points') || '').trim().split(/[\s,]+/).map(parseFloat).filter(function (v) { return isFinite(v); });
            if (pts.length < 4) return '';
            var d = 'M' + f(pts[0]) + ' ' + f(pts[1]);
            for (var i = 2; i + 1 < pts.length; i += 2) d += 'L' + f(pts[i]) + ' ' + f(pts[i + 1]);
            return d + (tg === 'polygon' ? 'Z' : '');
        }
        return '';
    }
    /* Линейный градиент заливки (url(#id) → <linearGradient>) — вектором: осевая заливка PDF.
       Радиальный, узор, градиент обводки — по-прежнему растр. */
    function gradientOf(n, fill) {
        var m = /url\(\s*["']?#([^"')]+)["']?\s*\)/.exec(fill || '');
        if (!m) return null;
        var g = (n.ownerSVGElement || n).querySelector('#' + (root.CSS && CSS.escape ? CSS.escape(m[1]) : m[1]));
        if (!g || g.tagName !== 'linearGradient' || g.hasAttribute('href') || g.hasAttribute('xlink:href') || g.hasAttribute('gradientTransform')) return null;
        function num(a, d) { var v = g.getAttribute(a); if (v == null) return d; return /%$/.test(v) ? parseFloat(v) / 100 : parseFloat(v); }
        var stops = [];
        g.querySelectorAll('stop').forEach(function (s) {
            var cs = getComputedStyle(s), c = toRgb(cs.stopColor || s.getAttribute('stop-color') || '#000');
            var off = s.getAttribute('offset'); off = off == null ? 0 : /%$/.test(off) ? parseFloat(off) / 100 : parseFloat(off);
            stops.push({ o: Math.max(0, Math.min(1, off || 0)), c: c });
        });
        if (!stops.length) return null;
        return { units: g.getAttribute('gradientUnits') || 'objectBoundingBox', x1: num('x1', 0), y1: num('y1', 0), x2: num('x2', 1), y2: num('y2', 0), stops: stops };
    }
    function isVectorSvg(svg) {
        var bad = svg.querySelector('text,image,use,foreignObject,pattern,mask,filter,radialGradient,marker,clipPath,switch');
        if (bad) return false;
        var els = svg.querySelectorAll('*');
        for (var i = 0; i < els.length; i++) {
            var cs = getComputedStyle(els[i]);   // CSS фигуры, не холст: фильтр, клип, маска — такую векторно не нарисовать
            if (/url\(/.test(cs.stroke) || (cs.getPropertyValue('filter') || 'none') !== 'none' || (cs.clipPath && cs.clipPath !== 'none') || (cs.mask && !/^none/.test(cs.mask))) return false;
            if (/url\(/.test(cs.fill) && !/^(stop|linearGradient|defs)$/.test(els[i].tagName) && !gradientOf(els[i], cs.fill)) return false;
        }
        return true;
    }
    function svgShapes(svg) {
        var shapes = [];
        svg.querySelectorAll('path,rect,circle,ellipse,line,polyline,polygon').forEach(function (n) {
            var cs = getComputedStyle(n);
            if (cs.display === 'none' || cs.visibility !== 'visible') return;
            var d = shapePath(n);
            if (!d) return;
            var m = n.getScreenCTM && n.getScreenCTM();
            if (!m) return;
            var grad = /url\(/.test(cs.fill) ? gradientOf(n, cs.fill) : null;
            var fill = cs.fill === 'none' || grad ? null : toRgb(cs.fill), stroke = cs.stroke === 'none' ? null : toRgb(cs.stroke);
            var op = cs.opacity === '' || cs.opacity == null ? 1 : +cs.opacity;
            var fo = (cs.fillOpacity === '' ? 1 : +cs.fillOpacity) * op * (fill ? fill.a : 1);
            var so = (cs.strokeOpacity === '' ? 1 : +cs.strokeOpacity) * op * (stroke ? stroke.a : 1);
            if (n.tagName === 'line' || n.tagName === 'polyline') { fill = null; grad = null; }
            if (!fill && !stroke && !grad) return;
            if (grad && grad.units !== 'userSpaceOnUse') {
                // доли коробки фигуры → её координаты (bbox в своей системе, как у браузера)
                var bb = null; try { bb = n.getBBox(); } catch (e) {}
                if (!bb || !(bb.width > 0) || !(bb.height > 0)) grad = null;
                else grad = { x1: bb.x + grad.x1 * bb.width, y1: bb.y + grad.y1 * bb.height, x2: bb.x + grad.x2 * bb.width, y2: bb.y + grad.y2 * bb.height, stops: grad.stops };
                if (!grad && !fill && !stroke) return;
            }
            shapes.push({
                grad: grad,
                d: d, m: { a: m.a, b: m.b, c: m.c, d: m.d, e: m.e, f: m.f }, fill: fill, stroke: stroke,
                sw: px(cs.strokeWidth) || 0, fo: fo, so: so, evenodd: cs.fillRule === 'evenodd',
                cap: cs.strokeLinecap, join: cs.strokeLinejoin,
                dash: cs.strokeDasharray && cs.strokeDasharray !== 'none' ? cs.strokeDasharray.split(/[\s,]+/).map(px) : null
            });
        });
        return shapes;
    }

    function markerOp(e, cs, r) {
        if (cs.listStyleImage && cs.listStyleImage !== 'none') { warn('list_image_skipped'); return; }
        var type = cs.listStyleType;
        if (!type || type === 'none') return;
        var list = e.parentElement, idx = 1;
        if (list && list.tagName === 'OL') {
            var rev = list.reversed, items = Array.prototype.filter.call(list.children, function (x) { return x.tagName === 'LI'; });
            var start = list.hasAttribute('start') ? +list.getAttribute('start') : (rev ? items.length : 1);
            idx = start;
            for (var i = 0; i < items.length && items[i] !== e; i++) {
                if (items[i].hasAttribute('value')) idx = +items[i].getAttribute('value');
                idx += rev ? -1 : 1;
            }
            if (e.hasAttribute('value')) idx = +e.getAttribute('value');
        } else if (list) {
            var k = 1;
            for (var s2 = e.previousElementSibling; s2; s2 = s2.previousElementSibling) if (s2.tagName === 'LI') k++;
            idx = k;
        }
        var mk = markerText(type, idx);
        if (!mk) return;
        var s = st(e);
        ops.push({ t: 'marker', el: e, r: r, mk: mk, s: mk.text || '', st: s, inside: cs.listStylePosition === 'inside', padL: px(cs.paddingLeft) + px(cs.borderLeftWidth), padR: px(cs.paddingRight) + px(cs.borderRightWidth) });
    }

    function visit(e) {
        var cs = st(e).cs;
        if (cs.display === 'none') return;
        if (e.hasAttribute && e.hasAttribute('data-hp-skip')) return;
        var tg = e.tagName;
        if (/^(SCRIPT|STYLE|TEMPLATE|NOSCRIPT|HEAD|META|LINK|DIALOG)$/.test(tg)) return;
        var visible = cs.visibility === 'visible';
        var br = e.getBoundingClientRect(), r = R(br);
        if (/^(page|always|left|right|recto|verso)$/.test(cs.breakBefore) && e !== el) forced.push(r.y);
        if (/^(page|always|left|right|recto|verso)$/.test(cs.breakAfter) && e !== el) forced.push(r.y + r.h);
        if ((tg === 'TR' || cs.breakInside === 'avoid' || cs.breakInside === 'avoid-page') && e !== el && r.h > 0) items.push({ t: r.y, b: r.y + r.h, avoid: true, el: e });
        if (visible) {
            var inline = /^inline/.test(cs.display) && cs.display !== 'inline-block' && cs.display !== 'inline-flex' && cs.display !== 'inline-grid' && cs.display !== 'inline-table';
            if (tg !== 'svg') boxOps(e, cs, r, inline, inline ? Array.prototype.map.call(e.getClientRects(), R) : null);
            if (tg === 'IMG' || tg === 'CANVAS' || tg === 'VIDEO') {
                if (r.w > 0 && r.h > 0) { ops.push({ t: 'img', el: e, r: r, cs: cs }); items.push({ t: r.y, b: r.y + r.h, el: e }); }
                return;
            }
            if (tg === 'svg') {
                if (r.w > 0 && r.h > 0) {
                    var vec = isVectorSvg(e);
                    ops.push(vec ? { t: 'svg', el: e, r: r, shapes: svgShapes(e) } : { t: 'img', el: e, r: r, cs: cs, svg: true });
                    if (!vec) warn('svg_rasterized');
                    items.push({ t: r.y, b: r.y + r.h, el: e });
                }
                return;
            }
            if (/^(INPUT|TEXTAREA|SELECT)$/.test(tg) && e.value) warn('form_value_skipped');
            if (cs.display === 'list-item') markerOp(e, cs, r);
            if (tg === 'A') {
                var href = e.getAttribute('href') || '';
                if (/^(https?:|mailto:|tel:)/i.test(href) || /^#./.test(href)) ops.push({ t: 'link', el: e, href: href, rects: Array.prototype.map.call(e.getClientRects(), R) });
            }
            if (!pseudoWarned) {
                var b4 = getComputedStyle(e, '::before').content, af = getComputedStyle(e, '::after').content;
                if ((b4 && b4 !== 'none' && b4 !== 'normal' && b4 !== '""') || (af && af !== 'none' && af !== 'normal' && af !== '""')) { pseudoWarned = true; warn('pseudo_content_skipped'); }
            }
        }
        for (var c = e.firstChild; c; c = c.nextSibling) {
            if (c.nodeType === 3) textNode(c);
            else if (c.nodeType === 1) visit(c);
        }
    }
    visit(el);

    // строки абзацев — для висячих строк и «заголовок с текстом»
    var byBlock = new Map();
    ops.forEach(function (op) {
        if (op.t !== 'text' || !op.blk) return;
        var arr = byBlock.get(op.blk); if (!arr) byBlock.set(op.blk, arr = []);
        arr.push(op);
    });
    byBlock.forEach(function (arr, b) {
        var lines = [];
        arr.forEach(function (op) {
            var base = op.r.y + op.bl, ln = null;
            for (var i = 0; i < lines.length; i++) if (Math.abs(lines[i].base - base) < op.r.h * 0.5) { ln = lines[i]; break; }
            if (!ln) { ln = { base: base, t: op.r.y, b: op.r.y + op.r.h }; lines.push(ln); }
            else { ln.t = Math.min(ln.t, op.r.y); ln.b = Math.max(ln.b, op.r.y + op.r.h); }
            op.line = ln;
        });
        lines.sort(function (a, c) { return a.base - c.base; });
        lines.forEach(function (ln, i) { items.push({ t: ln.t, b: ln.b, block: b.id, line: i, lines: lines.length, keepNext: b.keepNext && i === lines.length - 1 }); });
    });
    return { ops: ops, items: items, forced: forced, sc: sc, ox: ox, oy: oy, w: er.width / sc, h: er.height / sc, R: R, lang: lang, rtl: getComputedStyle(el).direction === 'rtl', face: st(el).face };
}

/* ── страницы ────────────────────────────────────────────────────────────── */
function layoutPages(el, M, o) {
    var mode = o.pages || (el.querySelector(o.pageSelector || '.hp-page') ? 'css' : 'single');
    var pages = [];
    if (mode === 'css' || mode === 'single') {
        var list = mode === 'css' ? Array.prototype.slice.call(el.querySelectorAll(o.pageSelector || '.hp-page')) : [el];
        if (mode === 'css' && el.matches && el.matches(o.pageSelector || '.hp-page')) list = [el];
        if (!list.length) list = [el];
        list.forEach(function (pe) {
            var r = M.R(pe.getBoundingClientRect());
            var sz = o.page ? pageMm(o.page, o.orientation) : null;
            var wPx = sz ? sz.w * MM : r.w, hPx = sz ? sz.h * MM : r.h;
            pages.push({ srcX0: r.x, srcY0: r.y, srcY1: r.y + r.h, srcX1: r.x + r.w, dstY0: 0, wPx: wPx, hPx: hPx, clip: [0, 0, wPx, hPx], twoD: true, el: pe });
        });
        return { mode: mode, pages: pages };
    }
    var sz2 = pageMm(o.page, o.orientation), W = sz2.w * MM, H = sz2.h * MM;
    if (mode === 'fixed') {
        var n = o.pageCount || Math.max(1, Math.ceil(M.h / H - 0.01));
        var mg = o.marginsMm != null ? margins(o.marginsMm) : null;
        for (var k = 0; k < n; k++) {
            var clip = mg ? [0, mg.t * MM - 2, W, H - (mg.t + mg.b) * MM + 4] : [0, 0, W, H];
            pages.push({ srcX0: 0, srcY0: k * H, srcY1: (k + 1) * H, dstY0: 0, wPx: W, hPx: H, clip: clip });
        }
        return { mode: mode, pages: pages };
    }
    // auto
    var m = margins(o.marginsMm), ch = H - (m.t + m.b) * MM;
    // «не разрывать» выше страницы не исполнить — такой блок режется по своим строкам
    var slices = planBreaks(M.items.filter(function (i) { return !i.avoid || i.b - i.t <= ch * 0.98; }), M.forced, M.h, ch);
    slices.forEach(function (s) {
        pages.push({ srcX0: -m.l * MM, srcY0: s.y0, srcY1: s.y1, dstY0: m.t * MM, wPx: W, hPx: H, clip: [0, m.t * MM, W, s.y1 - s.y0] });
    });
    return { mode: 'auto', pages: pages, margins: m };
}
function pagesOfRect(P, r, center) {
    var out = [];
    for (var k = 0; k < P.length; k++) {
        var p = P[k];
        if (center) {
            var cy = r.y + r.h / 2, cx = r.x + r.w / 2;
            if (cy >= p.srcY0 && cy < p.srcY1 && (!p.twoD || (cx >= p.srcX0 && cx < p.srcX1))) return [k];
            continue;
        }
        if (r.y < p.srcY1 && r.y + r.h > p.srcY0 && (!p.twoD || (r.x < p.srcX1 && r.x + r.w > p.srcX0))) out.push(k);
    }
    return out;
}

/* ── картинки ────────────────────────────────────────────────────────────── */
function jpegComponents(u) {
    for (var i = 2; i + 9 < u.length;) {
        if (u[i] !== 0xff) return 0;
        var mk = u[i + 1], len = (u[i + 2] << 8) | u[i + 3];
        if ((mk >= 0xc0 && mk <= 0xc3) || (mk >= 0xc5 && mk <= 0xc7) || (mk >= 0xc9 && mk <= 0xcb) || (mk >= 0xcd && mk <= 0xcf)) return u[i + 9];
        i += 2 + len;
    }
    return 0;
}
function canvasPng(c, type, q) {
    if (root.__canvasBlob) return root.__canvasBlob(c, type || 'image/png', q);
    // вне сайта (стенд, чужая страница) двери нет — data URL отвечает всегда
    return new Promise(function (res, rej) {
        try {
            var u = c.toDataURL(type || 'image/png', q), b = atob(u.split(',')[1]), a = new Uint8Array(b.length);
            for (var i = 0; i < b.length; i++) a[i] = b.charCodeAt(i);
            res(new Blob([a], { type: type || 'image/png' }));
        } catch (e) { rej(typed('CANVAS_ENCODE_FAILED')); }
    });
}
function decodeToCanvas(src, wPx, hPx, scale) {
    return new Promise(function (res) {
        var im = new Image();
        im.onload = function () {
            var w = Math.max(1, Math.round((wPx || im.naturalWidth) * scale)), h = Math.max(1, Math.round((hPx || im.naturalHeight) * scale));
            var c = document.createElement('canvas'); c.width = w; c.height = h;
            try { c.getContext('2d').drawImage(im, 0, 0, w, h); res(c); } catch (e) { res(null); }
        };
        im.onerror = function () { res(null); };
        im.src = src;
    });
}
function svgRaster(svg, r, dpi) {
    var clone = svg.cloneNode(true), src = svg.querySelectorAll('*'), dst = clone.querySelectorAll('*');
    var PROPS = ['fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width', 'stroke-opacity', 'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray', 'opacity', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-anchor', 'dominant-baseline', 'color', 'visibility', 'display'];
    function inline(a, b) { var cs = getComputedStyle(a); b.setAttribute('style', PROPS.map(function (p) { return p + ':' + cs.getPropertyValue(p); }).join(';')); }
    inline(svg, clone);
    for (var i = 0; i < src.length && i < dst.length; i++) inline(src[i], dst[i]);
    clone.setAttribute('width', r.w); clone.setAttribute('height', r.h);
    if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    var url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' }));
    return decodeToCanvas(url, r.w, r.h, dpi / 96).then(function (c) { URL.revokeObjectURL(url); return c; });
}
function embedImage(pdf, op, o, warn) {
    var e = op.el, dpi = o.imageDpi || 300, scale = dpi / 96;
    function fromCanvas(c) {
        if (!c) return null;
        return canvasPng(c, 'image/png').then(function (b) { return b.arrayBuffer(); }).then(function (ab) { return pdf.embedPng(new Uint8Array(ab)); }).catch(function () { warn('image_tainted'); return null; });
    }
    if (op.svg) return svgRaster(e, op.r, dpi).then(fromCanvas);
    if (e.tagName === 'CANVAS') return fromCanvas(e);
    if (e.tagName === 'VIDEO') {
        var c = document.createElement('canvas'); c.width = e.videoWidth || 1; c.height = e.videoHeight || 1;
        try { c.getContext('2d').drawImage(e, 0, 0, c.width, c.height); } catch (x) { return Promise.resolve(null); }
        return fromCanvas(c);
    }
    var src = e.currentSrc || e.src;
    var getBlob = o.imageBlob ? Promise.resolve(o.imageBlob(e)) : fetch(src).then(function (r) { if (!r.ok) throw new Error('img ' + r.status); return r.blob(); });
    return getBlob.then(function (b) {
        if (!b) return null;
        return b.arrayBuffer().then(function (ab) {
            var u = new Uint8Array(ab), natW = e.naturalWidth || 0, natH = e.naturalHeight || 0;
            var needW = op.r.w * scale, needH = op.r.h * scale;
            var huge = natW > needW * 2 && natH > needH * 2 && natW * natH > 4e6;
            var png = u[0] === 0x89 && u[1] === 0x50, jpg = u[0] === 0xff && u[1] === 0xd8;
            if (png && !huge) return pdf.embedPng(u);
            if (jpg && !huge && jpegComponents(u) !== 4) return pdf.embedJpg(u);
            // svg, webp, gif, avif, CMYK-JPEG, огромная фотография — через холст
            var url = URL.createObjectURL(b);
            var isSvg = /svg/.test(b.type) || (u[0] === 0x3c);
            var w = isSvg ? op.r.w : Math.min(natW || needW, needW), h = isSvg ? op.r.h : Math.min(natH || needH, needH);
            return decodeToCanvas(url, w, h, isSvg ? scale : 1).then(function (c) {
                URL.revokeObjectURL(url);
                if (!c) return null;
                if (jpg) return canvasPng(c, 'image/jpeg', 0.9).then(function (bb) { return bb.arrayBuffer(); }).then(function (a2) { return pdf.embedJpg(new Uint8Array(a2)); });
                return fromCanvas(c);
            });
        });
    }).catch(function () {
        // чужой адрес без CORS и т. п. — пробуем нарисовать уже загруженную картинку
        if (!e.complete || !e.naturalWidth) { warn('image_unreadable'); return null; }
        var c = document.createElement('canvas'); c.width = e.naturalWidth; c.height = e.naturalHeight;
        try { c.getContext('2d').drawImage(e, 0, 0); } catch (x) { warn('image_unreadable'); return null; }
        return fromCanvas(c);
    });
}
/* Куда встаёт картинка внутри своей коробки (object-fit/object-position). */
function fitBox(r, natW, natH, cs) {
    var fit = cs && cs.objectFit || 'fill';
    if (fit === 'fill' || !natW || !natH) return { x: r.x, y: r.y, w: r.w, h: r.h, clip: false };
    var s = fit === 'contain' ? Math.min(r.w / natW, r.h / natH) : fit === 'cover' ? Math.max(r.w / natW, r.h / natH) : fit === 'none' ? 1 : Math.min(1, Math.min(r.w / natW, r.h / natH));
    var w = natW * s, h = natH * s;
    var pos = String(cs.objectPosition || '50% 50%').split(/\s+/);
    function at(v, free) { if (/%$/.test(v)) return free * parseFloat(v) / 100; if (v === 'left' || v === 'top') return 0; if (v === 'right' || v === 'bottom') return free; if (v === 'center') return free / 2; return px(v); }
    return { x: r.x + at(pos[0] || '50%', r.w - w), y: r.y + at(pos[1] || '50%', r.h - h), w: w, h: h, clip: w > r.w + 0.5 || h > r.h + 0.5 };
}

/* ── сборка ──────────────────────────────────────────────────────────────── */
function render(el, opts) {
    var o = opts || {}, t0 = Date.now(), warnings = [];
    function warn(code) { if (warnings.indexOf(code) < 0) warnings.push(code); }
    function prog(f, phase) { try { if (o.onProgress) o.onProgress(f, phase); } catch (e) {} }
    var sig = o.signal;
    return load().then(function () {
        checkAbort(sig); prog(0.1, 'libs');
        var fr = document.fonts && document.fonts.ready ? bounded(document.fonts.ready, o.fontsWaitMs || 6000, 'fonts_ready').catch(function () {}) : null;
        var imgs = Array.prototype.slice.call(el.querySelectorAll('img'));
        return Promise.all([fr].concat(imgs.map(function (im) { return im.decode ? bounded(im.decode(), 10000, 'img_decode').catch(function () {}) : null; })));
    }).then(function () {
        checkAbort(sig);
        /* Готовят документ к выгрузке обычно в спрятанной копии (visibility:hidden за экраном —
           так делает /document-writer). Видимость наследуется, и такая копия измерялась бы
           пустой: на время замера (синхронно, кадра между ними нет) корень становится видимым —
           спрятанное ВНУТРИ документа так и остаётся спрятанным. */
        var vis = el.style.visibility, forced = getComputedStyle(el).visibility !== 'visible';
        if (forced) el.style.visibility = 'visible';
        var M, lay;
        try { M = measure(el, o, warn); lay = layoutPages(el, M, o); }
        finally { if (forced) el.style.visibility = vis; }
        prog(0.3, 'measure');
        return build(M, lay, o, warn, sig, prog);
    }).then(function (res) {
        prog(1, 'done');
        return { bytes: res.bytes, pages: res.pages, warnings: warnings, ms: Date.now() - t0 };
    });
}

function build(M, lay, o, warn, sig, prog) {
    var L = root.PDFLib, P = root.PdfTextFont, PG = lay.pages;
    return L.PDFDocument.create({ updateMetadata: false }).then(function (pdf) {
        pdf.registerFontkit(root.fontkit);
        pdf.__fontkitRegistered = true;
        var pgs = PG.map(function (p) { return pdf.addPage([p.wPx * PT, p.hPx * PT]); });
        function X(k, x) { return (x - PG[k].srcX0) * PT; }
        function Y(k, y) { return PG[k].hPx * PT - (PG[k].dstY0 + y - PG[k].srcY0) * PT; }

        // 0. колонтитулы и номера страниц — тоже текст, шрифты под них грузятся со всеми
        var deco = [];
        var baseSt = { face: M.face, color: { r: 0.25, g: 0.25, b: 0.25, a: 1 } };
        PG.forEach(function (p, k) {
            var list = [];
            if (o.footer) {
                var tx = typeof o.footer === 'function' ? o.footer(k + 1, PG.length) : String(o.footer).replace('{page}', k + 1).replace('{pages}', PG.length);
                if (tx) list.push({ text: String(tx), align: 'center', yPt: (lay.margins ? lay.margins.b * MM * PT / 2 : 8 * MM * PT) - 3, sizePt: o.footerSizePt || 9 });
            }
            if (o.header) {
                var hx = typeof o.header === 'function' ? o.header(k + 1, PG.length) : String(o.header).replace('{page}', k + 1).replace('{pages}', PG.length);
                if (hx) list.push({ text: String(hx), align: 'center', yPt: p.hPx * PT - (lay.margins ? lay.margins.t * MM * PT / 2 : 8 * MM * PT) - 3, sizePt: o.footerSizePt || 9 });
            }
            if (o.decorate) { try { (o.decorate({ page: k + 1, pages: PG.length, widthPt: p.wPx * PT, heightPt: p.hPx * PT }) || []).forEach(function (d) { list.push(d); }); } catch (e) {} }
            list.forEach(function (d) {
                if (!d || !d.text) return;
                var s = { face: d.face || baseSt.face, bold: !!d.bold, italic: false, sizePx: (d.sizePt || 9) / PT, color: d.color ? toRgb(d.color) : baseSt.color, ls: 0, rtl: false };
                deco.push({ k: k, d: d, op: { t: 'text', s: String(d.text), st: s, deco: null } });
            });
        });

        // 1. шрифты: метрические — по нужным начертаниям
        var metric = {};
        var needMetric = fontNeeder(pdf, metric);
        var textOps = M.ops.filter(function (x) { return x.t === 'text' || x.t === 'marker'; }).concat(deco.map(function (x) { return x.op; }));
        textOps.forEach(function (op) { planFaces(op, needMetric); });
        var fonts = {};
        return Promise.all(Object.keys(metric).map(function (k) { return metric[k].then(function (f) { fonts[k] = f; }); })).then(function () {
            checkAbort(sig); prog(0.45, 'fonts');
            // 2. куски слов: направление, затем покрытие шрифтом
            var fbNeed = {};
            textOps.forEach(function (op) { op.pieces = op.t === 'marker' && op.mk.shape ? [] : planPieces(op, fonts, fbNeed, M.lang); });
            return loadFallbacks(pdf, P, fbNeed, warn);
        }).then(function (fb) {
            checkAbort(sig); prog(0.6, 'fallback');
            // 3. картинки и знаки без шрифта — заранее
            var jobs = [];
            textOps.forEach(function (op) {
                op.pieces.forEach(function (pc) {
                    if (pc.kind === 'fb') { pc.font = fb[pc.fbKey] || null; if (!pc.font) pc.kind = 'img'; }
                    if (pc.kind === 'img') jobs.push(glyphImage(pdf, pc, op).then(function (im) { pc.img = im; }));
                });
            });
            M.ops.forEach(function (op) { if (op.t === 'img') jobs.push(embedImage(pdf, op, o, warn).then(function (im) { op.pdfImg = im; })); });
            return Promise.all(jobs);
        }).then(function () {
            checkAbort(sig); prog(0.75, 'images');
            draw(pdf, pgs, PG, M, lay, o, deco, X, Y, L);
            var rtlFonts = new Set();
            textOps.forEach(function (op) { op.pieces.forEach(function (pc) { if (pc.rtl && pc.font) rtlFonts.add(pc.font); }); });
            rtlLigatureText(rtlFonts);
            if (typeof o.debug === 'function') { try { o.debug({ ops: M.ops, pages: PG, sizes: textOps.map(function (op) { var w = 0; (op.pieces || []).forEach(function (pc) { w += pieceWidth(pc, op.st.sizePx * PT, op.st.ls); }); return { s: op.s, target: op.r ? op.r.w * PT : null, pdf: w, kinds: (op.pieces || []).map(function (pc) { return pc.kind; }).join(',') }; }) }); } catch (e) {} }
            prog(0.9, 'draw');
            return finish(pdf, o, L);
        }).then(function (bytes) { return { bytes: bytes, pages: PG.length }; });
    });
}

/* Загрузка начертаний на один документ: ключ → обещание встроенного шрифта. */
function fontNeeder(pdf, metric) {
    return function (face, bold, italic, wt) {
        var ff = faceFile(face, bold, italic, wt);
        if (!metric[ff.key]) metric[ff.key] = fetchBytes(ff.url, 'html_pdf_font').then(function (b) { return pdf.embedFont(b, { subset: true, customName: 'HP' + ff.key.replace(/[^a-z0-9]/gi, '') }); });
        return ff.key;
    };
}
function planFaces(op, needMetric) {
    op.mk1 = needMetric(op.st.face, op.st.bold, op.st.italic, op.st.wt);
    op.skew = faceFile(op.st.face, op.st.bold, op.st.italic, op.st.wt).skew;
    if (op.st.face === 'caladea') op.mk2 = needMetric('tinos', op.st.bold, op.st.italic);   // у Caladea нет кириллицы — как на экране
}

/* Слово → куски в ВИДИМОМ порядке: направление (bidi), затем — каким шрифтом. */
function needsBidi(s) { return RTL_RE.test(s) || NUM_AN.test(s); }
function prefFace(ch, lang) {
    if (/[\u0600-\u08ff\ufb50-\ufdff\ufe70-\ufeff]/.test(ch)) return 'arabic';
    if (/[\u1100-\u11ff\u3130-\u318f\uac00-\ud7af]/.test(ch)) return 'korean';
    if (/[\u3040-\u30ff\u31f0-\u31ff]/.test(ch)) return 'japanese';
    if (/[\u2e80-\u2fdf\u3000-\u303f\u3400-\u9fff\uf900-\ufaff\uff00-\uffef]/.test(ch)) return /^ja/i.test(lang || '') ? 'japanese' : 'cjk';
    return 'noto';
}
function planPieces(op, fonts, fbNeed, lang) {
    var P = root.PdfTextFont, s = op.s, out = [];
    var f1 = fonts[op.mk1], f2 = op.mk2 ? fonts[op.mk2] : null;
    var fk1 = f1 && f1.embedder && f1.embedder.font, fk2 = f2 && f2.embedder && f2.embedder.font;
    function has(fk, cp) { try { return !!(fk && fk.hasGlyphForCodePoint(cp)); } catch (e) { return false; } }
    /* Слово в абзаце справа налево получает направление АБЗАЦА, а не своё: «\u0662\u0660\u0662\u0667.» без единой
       буквы bidi читал бы слева направо, и точка вставала справа (браузер ставит её слева).
       Контекст — сильный знак впереди (арабская буква — для правила W2, иначе алеф), он
       убирается из результата. */
    var bidi = P && (needsBidi(s) || op.st.rtl), runs;
    if (bidi && op.st.rtl) {
        var pre = op.al ? '\u0627' : '\u05d0';
        runs = P.bidiRuns(pre + s).map(function (b) { return b.text.charAt(0) === pre ? { text: b.text.slice(1), rtl: b.rtl, an: b.an } : b; }).filter(function (b) { return b.text; });
    } else runs = bidi ? P.bidiRuns(s) : [{ text: s, rtl: false, an: false }];
    runs.forEach(function (b) {
        var t = bidi ? P.runText(b) : b.text;
        var parts = [], cur = null;
        Array.from(t).forEach(function (ch) {
            var cp = ch.codePointAt(0), kind, key = '';
            if (has(fk1, cp)) kind = 'm1';
            else if (fk2 && has(fk2, cp)) kind = 'm2';
            else if (/\s/.test(ch)) { kind = 'm1'; ch = has(fk1, 0x20) ? ' ' : ''; }
            else if (/[\u200c\u200d\u200e\u200f]/.test(ch) && cur && cur.kind === 'fb') kind = 'fb';
            else kind = 'fb';
            if (!ch) return;
            if (kind === 'fb') key = (cur && cur.kind === 'fb' && /[\u200c\u200d\p{M}]/u.test(ch)) ? cur.pref : prefFace(ch, lang);
            if (cur && cur.kind === kind && (kind !== 'fb' || cur.pref === key)) cur.text += ch;
            else { cur = { kind: kind, text: ch, pref: key }; parts.push(cur); }
        });
        if (b.rtl) parts.reverse();
        parts.forEach(function (p) {
            if (p.kind === 'm1') out.push({ kind: 'font', font: f1, text: p.text });
            else if (p.kind === 'm2') out.push({ kind: 'font', font: f2, text: p.text });
            else if (PICTO_RE.test(p.text)) out.push({ kind: 'img', text: p.text });
            else {
                var k = p.pref + (op.st.bold ? ':b' : '');
                fbNeed[k] = fbNeed[k] || { pref: p.pref, bold: op.st.bold, texts: [] };
                fbNeed[k].texts.push(p.text);
                out.push({ kind: 'fb', fbKey: null, pref: p.pref, text: p.text, rtl: b.rtl });
            }
        });
    });
    // запасной: какой гранью рисовать — решает таблица покрытия PdfTextFont (pick)
    out.forEach(function (pc) { if (pc.kind === 'fb') pc.fbKey = pc.pref + (op.st.bold ? ':b' : '') + '|' + pc.text; });
    return out;
}
function loadFallbacks(pdf, P, fbNeed, warn) {
    var map = {}, embedded = {};
    var keys = Object.keys(fbNeed);
    if (!keys.length || !P) return Promise.resolve(map);
    var all = keys.map(function (k) { return fbNeed[k].texts.join(''); }).join('');
    return P.ready(all).catch(function () { return null; }).then(function () {
        var jobs = [];
        keys.forEach(function (k) {
            var need = fbNeed[k];
            need.texts.forEach(function (t) {
                var id = P.pick(t, need.pref);
                if (!id || !P.canRender(t, id)) { map[need.pref + (need.bold ? ':b' : '') + '|' + t] = null; return; }
                var ek = id + (need.bold ? ':b' : '');
                if (!embedded[ek]) embedded[ek] = P.embed(pdf, id, { bold: need.bold }).catch(function () { warn('fallback_font_failed'); return null; });
                jobs.push(embedded[ek].then(function (f) { map[need.pref + (need.bold ? ':b' : '') + '|' + t] = f; }));
            });
        });
        return Promise.all(jobs).then(function () { return map; });
    });
}
/* Лигатура в письме справа налево (лам-алиф, «фа + йа», «ба + йа + нун» у IBM Plex Arabic) —
   один глиф на несколько букв. Глифы в файле стоят в ВИДИМОМ порядке, и извлекатель (pdf.js,
   PDFium) переворачивает строку обратно в логический — вместе с буквами ВНУТРИ лигатуры: слово
   «доллар» (дал-вав-лам-алиф-ра) копировалось с переставленными лам и алиф (замер стенда 21.10).
   Поэтому в ToUnicode буквы лигатуры записываются в видимом порядке: после переворота
   извлекателя выходят логические. Шрифт этого документа свой (fontkit.create на каждый
   embedFont), так что правка глифа не уходит в чужой файл. */
function rtlLigatureText(fonts) {
    fonts.forEach(function (f) {
        var em = f && f.embedder, list = em && (em.glyphs || []);
        var seen = new Set();
        (list || []).forEach(function (g) {
            if (!g || seen.has(g)) return;
            seen.add(g);
            var cps = g.codePoints;
            if (cps && cps.length > 1 && cps.every(function (cp) { return RTL_RE.test(String.fromCodePoint(cp)); })) {
                try { g.codePoints = cps.slice().reverse(); } catch (e) {}
            }
        });
    });
}
/* Знак, которого нет ни в одном шрифте (эмодзи), — картинкой с экранным шрифтом. */
function glyphImage(pdf, pc, op) {
    var cs = op.el ? getComputedStyle(op.el) : null;
    var sizePx = op.st.sizePx, k = 4;
    var font = cs ? ((cs.fontStyle === 'italic' ? 'italic ' : '') + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily) : (sizePx + 'px sans-serif');
    var c = document.createElement('canvas'), ctx = c.getContext('2d');
    ctx.font = font;
    var w = Math.max(1, ctx.measureText(pc.text).width), h = op.r ? op.r.h : sizePx * 1.2;
    pc.wPx = w; pc.hPx = h;
    c.width = Math.ceil(w * k); c.height = Math.ceil(h * k);
    ctx = c.getContext('2d'); ctx.scale(k, k); ctx.font = font;
    var col = op.st.color; ctx.fillStyle = 'rgb(' + Math.round(col.r * 255) + ',' + Math.round(col.g * 255) + ',' + Math.round(col.b * 255) + ')';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(pc.text, 0, op.bl != null ? op.bl : sizePx * 0.9);
    return canvasPng(c, 'image/png').then(function (b) { return b.arrayBuffer(); }).then(function (ab) { return pdf.embedPng(new Uint8Array(ab)); }).catch(function () { return null; });
}

/* ── глифы с кернингом ───────────────────────────────────────────────────
   pdf-lib пишет только номера глифов, и PDF ставит их по ширинам из /W — БЕЗ кернинга и без
   сдвигов значков (GPOS). Браузер кернит: «Устава,» на экране 30,77 pt, у pdf-lib 31,99 —
   слово наезжало на соседнее (замер стенда 21.10, Tinos). Поэтому раскладка берётся у того же
   fontkit с позициями, и разница «ширина из /W − настоящий шаг» уходит поправкой в TJ;
   значок со сдвигом вверх (ударение, огласовки) — отдельным Tj с подъёмом Ts. */
var shapeCache = typeof WeakMap === 'function' ? new WeakMap() : null;
function shaped(font, text) {
    var m = shapeCache && shapeCache.get(font);
    if (!m && shapeCache) shapeCache.set(font, m = {});
    if (m && m[text]) return m[text];
    var r = { codes: [], adv: [], w0: [], xo: [], yo: [], total: 0, rise: false };
    var hex = '';
    try { hex = String(font.encodeText(text).toString()).replace(/^<|>$/g, ''); } catch (e) { hex = ''; }
    var fk = font.embedder && font.embedder.font, lay = null;
    try { lay = fk ? fk.layout(text, font.embedder.fontFeatures) : null; } catch (e) { lay = null; }
    var n = hex.length / 4;
    if (lay && lay.glyphs.length === n && n > 0) {
        var k = 1000 / fk.unitsPerEm;
        for (var i = 0; i < n; i++) {
            var g = lay.glyphs[i], ps = lay.positions[i];
            r.codes.push(hex.substr(i * 4, 4));
            r.w0.push(g.advanceWidth * k);
            r.adv.push(ps.xAdvance * k);
            r.xo.push(ps.xOffset * k);
            r.yo.push(ps.yOffset * k);
            if (Math.abs(ps.yOffset) > 0.5) r.rise = true;
            r.total += ps.xAdvance * k;
        }
    } else {
        // не сошлось — без позиций, как рисует сам pdf-lib
        for (var j = 0; j < n; j++) { r.codes.push(hex.substr(j * 4, 4)); r.w0.push(0); r.adv.push(0); r.xo.push(0); r.yo.push(0); }
        var wd = 0; try { wd = font.widthOfTextAtSize(text, 1000); } catch (e2) {}
        r.total = wd; r.plain = true;
    }
    if (m) m[text] = r;
    return r;
}
/* Элементы TJ для одной формы: глифы и поправки (в тысячных кегля; плюс — влево). */
function tjElems(sh, L, out) {
    out = out || [];
    var buf = '';
    function num(v) {
        if (Math.abs(v) < 0.001) return;
        if (buf) { out.push(L.PDFHexString.of(buf)); buf = ''; }
        var last = out[out.length - 1];
        if (last && last instanceof L.PDFNumber) out[out.length - 1] = L.PDFNumber.of(last.asNumber() + v);
        else out.push(L.PDFNumber.of(v));
    }
    for (var i = 0; i < sh.codes.length; i++) {
        if (sh.plain) { buf += sh.codes[i]; continue; }
        if (sh.xo[i]) num(-sh.xo[i]);
        buf += sh.codes[i];
        num(sh.w0[i] - sh.adv[i] + sh.xo[i]);
    }
    if (buf) out.push(L.PDFHexString.of(buf));
    return out;
}
/* Поправка ВЛЕВО (кернинг «Ус», «AV» — 120–130 тысячных) кусками не больше 90: pypdf считает
   пробелом любую поправку от половины ширины пробела (|n| ≥ 0,475 пробела, в любую сторону) и
   выдавал «У става» (замер 21.10). Несколько чисел подряд — законный TJ, рисуется то же самое;
   поправки ВПРАВО (промежутки между словами) остаются целыми — там пробел и есть. */
var KERN_CHUNK = 90;
function splitKerns(arr, L) {
    var out = [];
    arr.forEach(function (x) {
        if (x instanceof L.PDFNumber && x.asNumber() > KERN_CHUNK) {
            var v = x.asNumber(), n = Math.ceil(v / KERN_CHUNK);
            for (var i = 0; i < n; i++) out.push(L.PDFNumber.of(v / n));
        } else out.push(x);
    });
    return out;
}
/* Операторы показа одной формы, когда есть подъём (Ts) — TJ кусками между значками. */
function riseOps(sh, sizePt, L) {
    var ops = [], cur = [];
    function flushTj() { if (cur.length) { ops.push({ tj: cur }); cur = []; } }
    for (var i = 0; i < sh.codes.length; i++) {
        var one = { codes: [sh.codes[i]], w0: [sh.w0[i]], adv: [sh.adv[i]], xo: [sh.xo[i]], yo: [0], plain: sh.plain };
        if (Math.abs(sh.yo[i]) > 0.5 && !sh.plain) {
            flushTj();
            ops.push({ rise: sh.yo[i] * sizePt / 1000 });
            ops.push({ tj: tjElems(one, L) });
            ops.push({ rise: 0 });
        } else tjElems(one, L, cur);
    }
    flushTj();
    return ops;
}
/* Есть ли у шрифта куска пробел: глиф .notdef в потоке запрещён PDF/A (6.2.11.8). */
function hasSpace(pc) {
    if (!pc || !pc.font || (pc.kind !== 'font' && pc.kind !== 'fb')) return false;
    try { return pc.font.embedder.font.hasGlyphForCodePoint(32); } catch (e) { return false; }
}
function pieceWidth(pc, sizePt, ls) {
    if (pc.kind === 'font' || pc.kind === 'fb') {
        if (!pc.font) return 0;
        var sh = shaped(pc.font, pc.text);
        return sh.total * sizePt / 1000 + (ls ? sh.codes.length * ls * PT : 0);
    }
    return (pc.wPx || 0) * PT;
}

function draw(pdf, pgs, PG, M, lay, o, deco, X, Y, L) {
    var ctx = pdf.context;
    var gsCache = {};
    function gs(k, a) {
        if (!(a < 0.999)) return null;
        var key = k + ':' + a.toFixed(3);
        if (!(key in gsCache)) gsCache[key] = pgs[k].maybeEmbedGraphicsState({ opacity: a, borderOpacity: a });
        return gsCache[key];
    }
    var pageOps = PG.map(function () { return { bg: [], fg: [] }; });
    M.ops.forEach(function (op) {
        if (op.t === 'text' || op.t === 'marker') {
            var ks = pagesOfRect(PG, op.t === 'text' ? op.r : (firstText(op) || op).r, true);
            if (ks.length) pageOps[ks[0]].fg.push(op);
        } else if (op.t === 'box') pagesOfRect(PG, op.r, false).forEach(function (k) {
            // в режиме листов фон «стола» вокруг листов на лист не попадает
            if (PG[k].el && op.el && !PG[k].el.contains(op.el)) return;
            pageOps[k].bg.push(op);
        });
        else if (op.t === 'link') { /* в конце */ }
        else pagesOfRect(PG, op.r, false).forEach(function (k) { pageOps[k].fg.push(op); });
    });
    function firstText(mo) {
        if (mo._ft !== undefined) return mo._ft;
        mo._ft = null;
        for (var i = 0; i < M.ops.length; i++) {
            var x = M.ops[i];
            if (x.t === 'text' && mo.el.contains(x.el)) { var li = x.el.closest('li'); if (!li || li === mo.el) { mo._ft = x; break; } }
        }
        return mo._ft;
    }
    function inside(k, r) { var p = PG[k]; return r.y >= p.srcY0 - 0.01 && r.y + r.h <= p.srcY1 + 0.01 && (!p.twoD || (r.x >= p.srcX0 - 0.01 && r.x + r.w <= p.srcX1 + 0.01)); }
    function clipOps(k) {
        var p = PG[k], c = p.clip;
        return [L.pushGraphicsState(), L.rectangle(c[0] * PT, p.hPx * PT - (c[1] + c[3]) * PT, c[2] * PT, c[3] * PT), L.clip(), L.endPath()];
    }
    function rgbOp(c, stroke) { return stroke ? L.setStrokingRgbColor(c.r, c.g, c.b) : L.setFillingRgbColor(c.r, c.g, c.b); }

    /* Путь прямоугольника со скруглением (в pt, y вверх). */
    function rrPath(x, y, w, h, rad) {
        var K = 0.5523, tl = Math.min(rad[0], w / 2, h / 2), tr = Math.min(rad[1], w / 2, h / 2), brr = Math.min(rad[2], w / 2, h / 2), bl = Math.min(rad[3], w / 2, h / 2);
        if (!(tl || tr || brr || bl)) return [L.rectangle(x, y, w, h)];
        var t = y + h, r = x + w;
        return [
            L.moveTo(x + tl, t), L.lineTo(r - tr, t), L.appendBezierCurve(r - tr + tr * K, t, r, t - tr + tr * K, r, t - tr),
            L.lineTo(r, y + brr), L.appendBezierCurve(r, y + brr - brr * K, r - brr + brr * K, y, r - brr, y),
            L.lineTo(x + bl, y), L.appendBezierCurve(x + bl - bl * K, y, x, y + bl - bl * K, x, y + bl),
            L.lineTo(x, t - tl), L.appendBezierCurve(x, t - tl + tl * K, x + tl - tl * K, t, x + tl, t), L.closePath()
        ];
    }
    function drawBox(k, op) {
        var pg = pgs[k], r = op.r, x = X(k, r.x), y = Y(k, r.y + r.h), w = r.w * PT, h = r.h * PT;
        var radPt = op.rad.map(function (v) { return v * PT; });
        if (op.bg) {
            var g = gs(k, op.bg.a);
            pg.pushOperators.apply(pg, [L.pushGraphicsState()].concat(g ? [L.setGraphicsState(g)] : [], [rgbOp(op.bg)], rrPath(x, y, w, h, radPt), [L.fill(), L.popGraphicsState()]));
        }
        var S = op.sides;
        if (!S.some(Boolean)) return;
        var uniform = S.every(function (s) { return s && s.w === S[0].w && s.style === S[0].style && s.c.r === S[0].c.r && s.c.g === S[0].c.g && s.c.b === S[0].c.b && s.c.a === S[0].c.a; });
        // схлопнутая рамка таблицы стоит на ребре ячейки серединой
        var half = op.collapse ? 0.5 : 0;
        if (uniform && (radPt.some(Boolean) || /dashed|dotted/.test(S[0].style)) && !op.collapse) {
            var bw = S[0].w * PT;
            var dash = S[0].style === 'dashed' ? [bw * 3, bw * 3] : S[0].style === 'dotted' ? [0, bw * 2] : null;
            var g2 = gs(k, S[0].c.a);
            pg.pushOperators.apply(pg, [L.pushGraphicsState()].concat(g2 ? [L.setGraphicsState(g2)] : [], [rgbOp(S[0].c, true), L.setLineWidth(bw)],
                dash ? [L.setDashPattern(dash, 0), L.setLineCap(S[0].style === 'dotted' ? L.LineCapStyle.Round : L.LineCapStyle.Butt)] : [],
                rrPath(x + bw / 2, y + bw / 2, w - bw, h - bw, radPt.map(function (v) { return Math.max(0, v - bw / 2); })), [L.stroke(), L.popGraphicsState()]));
            return;
        }
        S.forEach(function (s, i) {
            if (!s) return;
            var bw = s.w * PT, rx, ry, rw, rh, off = bw * half;
            if (i === 0) { rx = x; ry = y + h - bw + off; rw = w; rh = bw; }
            else if (i === 2) { rx = x; ry = y - off; rw = w; rh = bw; }
            else if (i === 3) { rx = x - off; ry = y; rw = bw; rh = h; }
            else { rx = x + w - bw + off; ry = y; rw = bw; rh = h; }
            var g3 = gs(k, s.c.a), pre = [L.pushGraphicsState()].concat(g3 ? [L.setGraphicsState(g3)] : []);
            if (/dashed|dotted/.test(s.style)) {
                var horiz = i === 0 || i === 2, dash2 = s.style === 'dashed' ? [bw * 3, bw * 3] : [0, bw * 2];
                var a = horiz ? [rx, ry + rh / 2, rx + rw, ry + rh / 2] : [rx + rw / 2, ry, rx + rw / 2, ry + rh];
                pg.pushOperators.apply(pg, pre.concat([rgbOp(s.c, true), L.setLineWidth(bw), L.setDashPattern(dash2, 0), L.setLineCap(s.style === 'dotted' ? L.LineCapStyle.Round : L.LineCapStyle.Butt),
                    L.moveTo(a[0], a[1]), L.lineTo(a[2], a[3]), L.stroke(), L.popGraphicsState()]));
            } else if (s.style === 'double' && bw >= 2.2) {
                var t3 = bw / 3, horiz2 = i === 0 || i === 2;
                pg.pushOperators.apply(pg, pre.concat([rgbOp(s.c)],
                    horiz2 ? [L.rectangle(rx, ry, rw, t3), L.rectangle(rx, ry + rh - t3, rw, t3)] : [L.rectangle(rx, ry, t3, rh), L.rectangle(rx + rw - t3, ry, t3, rh)],
                    [L.fill(), L.popGraphicsState()]));
            } else {
                pg.pushOperators.apply(pg, pre.concat([rgbOp(s.c), L.rectangle(rx, ry, rw, rh), L.fill(), L.popGraphicsState()]));
            }
        });
    }

    /* ── текст: строка одного шрифта — одним текстовым объектом (TJ) ── */
    var run = null;
    // имя шрифта в ресурсах листа — одно на шрифт (page.setFont заводит НОВОЕ имя на каждый вызов)
    var fkCache = pgs.map(function () { return new Map(); });
    function fontKey(k, font) {
        var m = fkCache[k];
        if (!m.has(font)) { pgs[k].setFont(font); m.set(font, pgs[k].fontKey); }
        return m.get(font);
    }
    function showOps(ctxArr) {
        // { tj: [...] } | { rise: n } → операторы
        return ctxArr.map(function (x) {
            if (x.rise != null) return L.setTextRise(x.rise);
            return L.PDFOperator.of(L.PDFOperatorNames.ShowTextAdjusted, [pdf.context.obj(splitKerns(x.tj, L))]);
        });
    }
    function flush() {
        if (!run) return;
        var pg = pgs[run.k];
        var key = fontKey(run.k, run.font), size = run.size, arr = [], cursor = run.items[0].x;
        run.items.forEach(function (it, i) {
            if (i) { var gap = it.x - cursor; if (Math.abs(gap) > 0.005) arr.push(L.PDFNumber.of(-gap * 1000 / size)); }
            tjElems(it.sh, L, arr);
            cursor = it.x + it.w;
        });
        var g = gs(run.k, run.color.a);
        pg.pushOperators.apply(pg, [].concat(g ? [L.pushGraphicsState(), L.setGraphicsState(g)] : [], [
            L.beginText(), rgbOp(run.color), L.setFontAndSize(key, size)],
            // Tc и Tz — состояние ГРАФИКИ, оно переживает ET: ставятся в каждом объекте явно
            [L.setCharacterSpacing(run.ls || 0), L.setCharacterSqueeze(100)],
            [L.setTextMatrix(1, 0, run.skew, 1, run.items[0].x, run.base),
            L.PDFOperator.of(L.PDFOperatorNames.ShowTextAdjusted, [pdf.context.obj(splitKerns(arr, L))]),
            L.endText()], g ? [L.popGraphicsState()] : []));
        run = null;
    }
    function addRun(k, font, sizePt, color, base, xPt, sh, w, ls, skew) {
        if (run && (run.k !== k || run.font !== font || Math.abs(run.size - sizePt) > 0.01 || Math.abs(run.base - base) > 0.4 || run.ls !== ls || run.skew !== skew ||
            run.color.r !== color.r || run.color.g !== color.g || run.color.b !== color.b || run.color.a !== color.a || xPt < run.items[run.items.length - 1].x)) flush();
        if (!run) run = { k: k, font: font, size: sizePt, color: color, base: base, items: [], ls: ls, skew: skew };
        run.items.push({ x: xPt, sh: sh, w: w });
    }
    function drawPieces(k, pieces, x0, base, sizePt, color, ls, skew, squeeze, imgTopY, trailSpace, italic) {
        var pg = pgs[k], x = x0, skew0 = skew;
        pieces.forEach(function (pc, i) {
            // у запасных начертаний нет курсива — браузер наклоняет их сам, так и здесь
            skew = italic && pc.kind === 'fb' ? 0.25 : skew0;
            var w = pieceWidth(pc, sizePt, ls);
            if (pc.kind === 'img') {
                if (pc.img) pg.drawImage(pc.img, { x: x, y: imgTopY - (pc.hPx || 0) * PT, width: w * squeeze / 100, height: (pc.hPx || 0) * PT });
                x += w * squeeze / 100;
                return;
            }
            if (!pc.font) return;
            var sh = shaped(pc.font, pc.text + (trailSpace && i === pieces.length - 1 ? ' ' : ''));
            var body = sh.rise ? showOps(riseOps(sh, sizePt, L)) : [L.PDFOperator.of(L.PDFOperatorNames.ShowTextAdjusted, [pdf.context.obj(splitKerns(tjElems(sh, L), L))])];
            var fkey = fontKey(k, pc.font);
            var g = gs(k, color.a);
            pg.pushOperators.apply(pg, [].concat(g ? [L.pushGraphicsState(), L.setGraphicsState(g)] : [], [
                L.beginText(), rgbOp(color), L.setFontAndSize(fkey, sizePt)],
                [L.setCharacterSpacing(ls ? ls * PT : 0), L.setCharacterSqueeze(Math.abs(squeeze - 100) > 0.05 ? squeeze : 100), L.setTextRise(0)],
                [L.setTextMatrix(1, 0, skew, 1, x, base)], body, [L.endText()],
                g ? [L.popGraphicsState()] : []));
            x += w * squeeze / 100;
        });
        return x - x0;
    }
    var lines = [];
    function drawText(k, op) {
        var s = op.st, sizePt = s.sizePx * PT, base = Y(k, op.r.y + op.bl), color = s.color;
        var x0 = X(k, op.r.x), target = op.r.w * PT, ls = s.ls;
        var skew = op.skew != null ? op.skew : (s.italic && s.face === 'cousine' ? 0.25 : 0);
        var pcs = op.pieces, total = 0;
        pcs.forEach(function (pc) { total += pieceWidth(pc, sizePt, ls); });
        if (!(total > 0)) return;
        var rtl = pcs.some(function (pc) { return pc.rtl; }) || s.rtl;
        var simple = pcs.length === 1 && pcs[0].kind === 'font' && !rtl && !shaped(pcs[0].font, pcs[0].text).rise && Math.abs(total - target) <= Math.max(0.4, target * 0.015);
        if (simple) {
            var str = pcs[0].text + (op.trail ? ' ' : '');
            var shs = shaped(pcs[0].font, str);
            addRun(k, pcs[0].font, sizePt, color, base, x0, shs, pieceWidth({ kind: 'font', font: pcs[0].font, text: str }, sizePt, ls), ls * PT, skew);
        } else {
            flush();
            // слово чужим шрифтом вписывается в место, которое ему отвёл браузер
            var sq = Math.max(50, Math.min(200, target / total * 100));
            // Шире своего знака слово слева направо не растягиваем больше чем на 10 %: лишняя ширина
            // прямоугольника — это растяжка выключки по ширине, которую Blink приписывает букве у
            // встроенного блока (бланк /car-sale-contract: «№» между линиями — 26 px против 15 у
            // знака, в PDF выходил широким и «жирным», QA 23.10.2026). Слово встаёт в начало
            // своего места, остаток — пробел, как на экране. Справа налево — как было.
            if (!rtl && sq > 110) sq = 110;
            drawPieces(k, pcs, x0, base, sizePt, color, ls, skew, sq, Y(k, op.r.y), op.trail && !rtl && hasSpace(pcs[pcs.length - 1]), s.italic);
            // справа налево пробел после слова стоит СЛЕВА от него: настоящий пробел там же —
            // извлекатели (MuPDF) иначе склеивали арабские слова в одно
            var lp = pcs[0];
            if (rtl && op.trail && hasSpace(lp)) {
                var spw = pieceWidth({ kind: lp.kind, font: lp.font, text: ' ' }, sizePt, 0);
                if (spw > 0) drawPieces(k, [{ kind: lp.kind, font: lp.font, text: ' ' }], x0 - spw, base, sizePt, color, 0, 0, 100, Y(k, op.r.y), false, false);
            }
        }
        if (op.deco) {
            var th = Math.max(0.5, s.sizePx * 0.055) * PT, dc = op.deco.color || color;
            op.deco.lines.forEach(function (t) {
                var yy = t === 'underline' ? base - sizePt * 0.12 : t === 'line-through' ? base + sizePt * 0.28 : base + sizePt * 0.78;
                lines.push({ k: k, x1: x0, x2: x0 + target, y: yy, th: th, c: dc });
            });
        }
    }
    function drawMarker(k, op) {
        var ft = firstText(op), s = op.st, sizePx = ft ? ft.st.sizePx : s.sizePx, color = s.color;
        var lineTop = ft ? ft.r.y : op.r.y, bl = ft ? ft.bl : sizePx * 0.9;
        var baseY = Y(k, lineTop + bl);
        var rtl = s.rtl;
        var edge = op.inside && ft ? (rtl ? ft.r.x + ft.r.w : ft.r.x) : (rtl ? op.r.x + op.r.w - op.padR : op.r.x + op.padL);
        var mk = op.mk;
        if (mk.shape) {
            /* Геометрия Chromium (замер 21.10, Tinos/Arimo/Cousine, 10–32 px): диаметр — треть
               высоты над базовой линией, правый край — на 7 px + диаметр левее начала текста,
               центр — на диаметр выше базовой линии; у кружка обводка 1 px. */
            var D = bl / 3, gapPx = 7 + D / 2;
            var cx = rtl ? edge + gapPx : edge - gapPx;
            var cy = Y(k, lineTop + bl - D), rr = D / 2, pg = pgs[k], col = L.rgb(color.r, color.g, color.b);
            if (mk.shape === 'square') pg.drawRectangle({ x: X(k, cx - rr), y: cy - rr * PT, width: 2 * rr * PT, height: 2 * rr * PT, color: col });
            else if (mk.shape === 'circle') pg.drawEllipse({ x: X(k, cx), y: cy, xScale: (rr - 0.5) * PT, yScale: (rr - 0.5) * PT, borderColor: col, borderWidth: PT });
            else pg.drawEllipse({ x: X(k, cx), y: cy, xScale: rr * PT, yScale: rr * PT, color: col });
            return;
        }
        var pcs = op.pieces, sizePt = sizePx * PT, w = 0;
        pcs.forEach(function (pc) { w += pieceWidth(pc, sizePt, 0); });
        var sp = pcs[0] && pcs[0].font ? pieceWidth({ kind: 'font', font: pcs[0].font, text: ' ' }, sizePt, 0) : sizePt * 0.25;
        var xPt = rtl ? X(k, edge) + sp : X(k, edge) - sp - w;
        if (op.inside) xPt = rtl ? X(k, edge) + sp : X(k, edge) - sp - w;
        flush();
        drawPieces(k, pcs, xPt, baseY, sizePt, color, 0, 0, 100, Y(k, lineTop), false);
    }
    function drawImg(k, op) {
        if (!op.pdfImg) return;
        var pg = pgs[k], e = op.el;
        var natW = e.naturalWidth || e.width || op.pdfImg.width, natH = e.naturalHeight || e.height || op.pdfImg.height;
        var cs = op.cs, pad = { t: px(cs.paddingTop) + px(cs.borderTopWidth), r: px(cs.paddingRight) + px(cs.borderRightWidth), b: px(cs.paddingBottom) + px(cs.borderBottomWidth), l: px(cs.paddingLeft) + px(cs.borderLeftWidth) };
        var content = { x: op.r.x + pad.l, y: op.r.y + pad.t, w: op.r.w - pad.l - pad.r, h: op.r.h - pad.t - pad.b };
        var fb = op.svg || e.tagName === 'CANVAS' ? { x: content.x, y: content.y, w: content.w, h: content.h, clip: false } : fitBox(content, natW, natH, cs);
        var needClip = fb.clip || !inside(k, op.r);
        if (needClip) {
            var c = fb.clip ? [L.pushGraphicsState(), L.rectangle(X(k, content.x), Y(k, content.y + content.h), content.w * PT, content.h * PT), L.clip(), L.endPath()] : [];
            if (!inside(k, op.r)) c = clipOps(k).concat(c);
            pg.pushOperators.apply(pg, c);
            pg.drawImage(op.pdfImg, { x: X(k, fb.x), y: Y(k, fb.y + fb.h), width: fb.w * PT, height: fb.h * PT });
            for (var qn = c.length / 4; qn > 0; qn--) pg.pushOperators(L.popGraphicsState());
        } else pg.drawImage(op.pdfImg, { x: X(k, fb.x), y: Y(k, fb.y + fb.h), width: fb.w * PT, height: fb.h * PT });
    }
    /* Градиент: путь как клип (W n) и осевая заливка `sh` в координатах SVG — внутри q…Q самого
       drawSvgPath, где система уже приведена к пространству фигуры (scale 1,−1). */
    function gradientFill(pg, d, g, evenodd) {
        var name = shadingName(pg, shadingRef(ctx, g, L), L);
        // путь клипует, заливает осевая заливка — тем же нарезанным сборщиком (svgPathSliced)
        svgPathSliced(pg, d, { shading: name, fillRule: evenodd ? 'evenodd' : undefined }, L);
    }
    function drawSvg(k, op) {
        var pg = pgs[k], p = PG[k];
        var pre = inside(k, op.r) ? [] : clipOps(k);
        if (pre.length) pg.pushOperators.apply(pg, pre);
        op.shapes.forEach(function (sh) {
            var mtx = svgMatrix(sh.m, PT, M.ox, M.oy, p.hPx * PT, p.srcX0, p.srcY0, p.dstY0, M.sc);
            var so = {
                x: 0, y: 0, color: sh.fill ? L.rgb(sh.fill.r, sh.fill.g, sh.fill.b) : undefined, borderColor: sh.stroke ? L.rgb(sh.stroke.r, sh.stroke.g, sh.stroke.b) : undefined,
                borderWidth: sh.stroke ? sh.sw : 0, borderDashArray: sh.dash || undefined, fillRule: sh.evenodd ? 'evenodd' : undefined,
                borderLineCap: sh.cap === 'round' ? L.LineCapStyle.Round : sh.cap === 'square' ? L.LineCapStyle.Projecting : undefined,
                opacity: sh.fo < 0.999 ? sh.fo : undefined, borderOpacity: sh.so < 0.999 ? sh.so : undefined
            };
            // матрица формы (getScreenCTM → лист) — снаружи; сам путь — через дверь сайта
            // __pdfSvgPath (кусками: длинный путь не роняет стек), вне сайта — той же нарезкой здесь
            pg.pushOperators(L.pushGraphicsState(), L.concatTransformationMatrix(mtx[0], mtx[1], mtx[2], mtx[3], mtx[4], mtx[5]));
            try {
                if (sh.grad) {
                    gradientFill(pg, sh.d, sh.grad, sh.evenodd);
                    if (sh.stroke) { so.color = undefined; so.fillRule = undefined; }
                }
                if (!sh.grad || sh.stroke) {
                    if (typeof root.__pdfSvgPath === 'function') root.__pdfSvgPath(pg, sh.d, so, L);
                    else svgPathSliced(pg, sh.d, so, L);
                }
            } catch (e) {}
            pg.pushOperators(L.popGraphicsState());
        });
        if (pre.length) pg.pushOperators(L.popGraphicsState());
    }

    PG.forEach(function (p, k) {
        var po = pageOps[k];
        // 1. фоны и рамки — под всем остальным, обрезаны полем страницы
        if (po.bg.length) {
            pgs[k].pushOperators.apply(pgs[k], clipOps(k));
            po.bg.forEach(function (op) { drawBox(k, op); });
            pgs[k].pushOperators(L.popGraphicsState());
        }
        // 2. остальное — в порядке чтения (так текст и извлекается по порядку)
        po.fg.forEach(function (op) {
            if (op.t === 'text') return drawText(k, op);
            flush();
            if (op.t === 'img') drawImg(k, op);
            else if (op.t === 'svg') drawSvg(k, op);
            else if (op.t === 'marker') drawMarker(k, op);
        });
        flush();
        deco.forEach(function (dd) {
            if (dd.k !== k) return;
            var d = dd.d, op = dd.op, sizePt = op.st.sizePx * PT, w = 0;
            op.pieces.forEach(function (pc) { w += pieceWidth(pc, sizePt, 0); });
            var xPt = d.xPt != null ? d.xPt : (p.wPx * PT - w) / 2;
            if (d.align === 'right') xPt = (d.xPt != null ? d.xPt : p.wPx * PT - 15 * MM * PT) - w;
            else if (d.align === 'left' && d.xPt == null) xPt = 15 * MM * PT;
            else if (d.align === 'center' && d.xPt != null) xPt = d.xPt - w / 2;
            drawPieces(k, op.pieces, xPt, d.yPt, sizePt, op.st.color, 0, 0, 100, d.yPt + sizePt, false);
        });
    });
    // подчёркнутые слова одной строки — одной линией (браузер подчёркивает и пробелы между ними)
    var merged = [];
    lines.forEach(function (l) {
        var m = merged[merged.length - 1];
        if (m && m.k === l.k && Math.abs(m.y - l.y) < 0.3 && Math.abs(m.th - l.th) < 0.01 && m.c.r === l.c.r && m.c.g === l.c.g && m.c.b === l.c.b && l.x1 >= m.x2 - 0.5 && l.x1 - m.x2 < m.th * 30) m.x2 = Math.max(m.x2, l.x2);
        else merged.push({ k: l.k, x1: l.x1, x2: l.x2, y: l.y, th: l.th, c: l.c });
    });
    lines = merged;
    lines.forEach(function (l) { pgs[l.k].drawLine({ start: { x: l.x1, y: l.y }, end: { x: l.x2, y: l.y }, thickness: l.th, color: L.rgb(l.c.r, l.c.g, l.c.b) }); });

    // 3. ссылки — аннотации (Print-флаг: PDF/A требует /F 4)
    var refs = pgs.map(function (pg) { return pg.ref; });
    M.ops.forEach(function (op) {
        if (op.t !== 'link') return;
        var dest = null;
        if (op.href.charAt(0) === '#') {
            var tgt = null;
            try { tgt = document.getElementById(decodeURIComponent(op.href.slice(1))); } catch (e) {}
            if (!tgt || !M.R) return;
            var tr = M.R(tgt.getBoundingClientRect()), tk = pagesOfRect(PG, { x: tr.x, y: tr.y, w: Math.max(1, tr.w), h: 1 }, true)[0];
            if (tk == null) return;
            dest = [refs[tk], L.PDFName.of('XYZ'), null, Y(tk, tr.y), null];
        }
        op.rects.forEach(function (r) {
            if (!(r.w > 0 && r.h > 0)) return;
            var k = pagesOfRect(PG, r, true)[0];
            if (k == null) return;
            try {
                var d = { Type: 'Annot', Subtype: 'Link', F: 4, Border: [0, 0, 0], Rect: [X(k, r.x), Y(k, r.y + r.h), X(k, r.x + r.w), Y(k, r.y)] };
                if (dest) d.Dest = dest;
                else d.A = { Type: 'Action', S: 'URI', URI: L.PDFString.of(op.href) };
                var ref = ctx.register(ctx.obj(d));
                var node = pgs[k].node;
                if (typeof node.addAnnot === 'function') node.addAnnot(ref);
                else { var an = node.lookup(L.PDFName.of('Annots')); if (an) an.push(ref); else node.set(L.PDFName.of('Annots'), ctx.obj([ref])); }
            } catch (e) {}
        });
    });
}

/* Осевая заливка (ShadingType 2) по стопам градиента: два стопа — одна функция типа 2,
   больше — сшивка типа 3. Прозрачность стопов не переносится (нужна маска мягкости). */
function shadingRef(ctx, g, L) {
    var st = g.stops.slice().sort(function (a, b) { return a.o - b.o; });
    if (st.length === 1) st = [st[0], st[0]];
    if (st[0].o > 0) st.unshift({ o: 0, c: st[0].c });
    if (st[st.length - 1].o < 1) st.push({ o: 1, c: st[st.length - 1].c });
    function cf(c) { return [c.r, c.g, c.b]; }
    var fns = [], bounds = [], enc = [];
    for (var i = 0; i < st.length - 1; i++) {
        fns.push(ctx.obj({ FunctionType: 2, Domain: [0, 1], C0: cf(st[i].c), C1: cf(st[i + 1].c), N: 1 }));
        if (i > 0) bounds.push(st[i].o);
        enc.push(0, 1);
    }
    var fn = fns.length === 1 ? fns[0] : ctx.obj({ FunctionType: 3, Domain: [0, 1], Functions: fns, Bounds: bounds, Encode: enc });
    return ctx.register(ctx.obj({ ShadingType: 2, ColorSpace: 'DeviceRGB', Coords: [g.x1, g.y1, g.x2, g.y2], Function: fn, Extend: [true, true] }));
}
var shadingSeq = 0;
function shadingName(pg, ref, L) {
    var res = pg.node.Resources(), PN = L.PDFName.of;
    var sd = res.lookup(PN('Shading'));
    if (!sd) { sd = pg.doc.context.obj({}); res.set(PN('Shading'), sd); }
    var name = 'Sh' + (++shadingSeq);
    sd.set(PN(name), ref);
    return name;
}

/* Путь SVG без двери сайта (стенд, страница без timbrica-core): те же операторы pdf-lib, в
   поток — кусками по 4096 (одним вызовом длинный путь роняет стек, см. __pdfSvgPath). */
function svgPathSliced(pg, d, o, L) {
    var build = L['drawSvgPath'];
    var g = o.opacity != null || o.borderOpacity != null ? pg.maybeEmbedGraphicsState({ opacity: o.opacity, borderOpacity: o.borderOpacity }) : undefined;
    var ops = build(d, { x: 0, y: 0, color: o.color, borderColor: o.borderColor, borderWidth: o.borderWidth, borderDashArray: o.borderDashArray, borderLineCap: o.borderLineCap, graphicsState: g });
    if (o.shading) {
        // заливка градиентом (HtmlPdf.slides): оператор краски перед закрывающим Q → W n sh
        var NS = L.PDFOperatorNames, qs = ops.length - 2;
        ops.splice(qs, 1, L.PDFOperator.of(o.fillRule === 'evenodd' ? NS.ClipEvenOdd : NS.ClipNonZero), L.endPath(), L.PDFOperator.of(NS.ShadingFill, [L.PDFName.of(o.shading)]));
    } else if (o.fillRule === 'evenodd') {
        var N = L.PDFOperatorNames, q = ops.length - 2, nm = ops[q] && ops[q].name;
        if (nm === N.FillNonZero) ops[q] = L.PDFOperator.of(N.FillEvenOdd);
        else if (nm === N.FillNonZeroAndStroke) ops[q] = L.PDFOperator.of(N.FillEvenOddAndStroke);
    }
    for (var i = 0; i < ops.length; i += 4096) pg.pushOperators.apply(pg, ops.slice(i, i + 4096));
}

/* ── метаданные, PDF/A, вложения, сохранение ─────────────────────────────── */
function hex16() {
    var u = new Uint8Array(16);
    if (root.crypto && root.crypto.getRandomValues) root.crypto.getRandomValues(u); else for (var i = 0; i < 16; i++) u[i] = Math.floor(Math.random() * 256);
    return Array.from(u).map(function (b) { return (b < 16 ? '0' : '') + b.toString(16); }).join('');
}
function addAttachment(pdf, a, L) {
    var ctx = pdf.context;
    var bytes = a.bytes instanceof Uint8Array ? a.bytes : new Uint8Array(a.bytes);
    var date = a.modDate || new Date();
    var name = String(a.name || 'attachment.bin');
    var ef = ctx.flateStream(bytes, {
        Type: 'EmbeddedFile', Subtype: String(a.mime || 'application/octet-stream'),
        Params: { Size: bytes.length, ModDate: L.PDFString.fromDate(date), CreationDate: L.PDFString.fromDate(a.creationDate || date) }
    });
    var efRef = ctx.register(ef);
    var spec = ctx.obj({
        Type: 'Filespec', F: L.PDFString.of(name.replace(/[^\x20-\x7e]/g, '_')), UF: L.PDFHexString.fromText(name),
        EF: { F: efRef, UF: efRef }, Desc: L.PDFHexString.fromText(String(a.description || name)),
        AFRelationship: String(a.afRelationship || 'Unspecified')
    });
    var specRef = ctx.register(spec);
    var cat = pdf.catalog, PN = L.PDFName.of;
    var names = cat.lookup(PN('Names'));
    if (!names) { names = ctx.obj({}); cat.set(PN('Names'), names); }
    var efTree = names.lookup(PN('EmbeddedFiles'));
    if (!efTree) { efTree = ctx.obj({ Names: [] }); names.set(PN('EmbeddedFiles'), efTree); }
    var arr = efTree.lookup(PN('Names'));
    if (!arr) { arr = ctx.obj([]); efTree.set(PN('Names'), arr); }
    // дерево имён упорядочено по имени
    var pairs = [];
    for (var i = 0; i + 1 < arr.size(); i += 2) pairs.push([arr.get(i), arr.get(i + 1)]);
    pairs.push([L.PDFHexString.fromText(name), specRef]);
    pairs.sort(function (p, q) { var a1 = p[0].decodeText ? p[0].decodeText() : String(p[0]), b1 = q[0].decodeText ? q[0].decodeText() : String(q[0]); return a1 < b1 ? -1 : a1 > b1 ? 1 : 0; });
    var na = ctx.obj([]);
    pairs.forEach(function (p) { na.push(p[0]); na.push(p[1]); });
    efTree.set(PN('Names'), na);
    var af = cat.lookup(PN('AF'));
    if (!af) { af = ctx.obj([]); cat.set(PN('AF'), af); }
    af.push(specRef);
    if (a.showPanel !== false && !cat.lookup(PN('PageMode'))) cat.set(PN('PageMode'), PN('UseAttachments'));
}
function finish(pdf, o, L) {
    var now = o.date || new Date();
    var site = '';
    try { var sm = document.querySelector('meta[property="og:site_name"]'); site = sm ? sm.content : ''; } catch (e) {}
    var meta = {
        title: o.title != null ? String(o.title) : null, author: o.author != null ? String(o.author) : null,
        subject: o.subject != null ? String(o.subject) : null, keywords: o.keywords != null ? [].concat(o.keywords).join(', ') : null,
        creator: o.creator != null ? String(o.creator) : (site || 'HtmlPdf'), producer: o.producer != null ? String(o.producer) : (site ? site + ' (HtmlPdf ' + VERSION + ')' : 'HtmlPdf ' + VERSION),
        lang: o.lang || '', created: now, modified: now
    };
    if (meta.title) pdf.setTitle(meta.title, { showInWindowTitleBar: true });
    if (meta.author != null) pdf.setAuthor(meta.author);
    if (meta.subject != null) pdf.setSubject(meta.subject);
    if (meta.keywords != null) pdf.setKeywords([meta.keywords]);
    pdf.setCreator(meta.creator); pdf.setProducer(meta.producer);
    pdf.setCreationDate(now); pdf.setModificationDate(now);
    if (meta.lang && pdf.setLanguage) pdf.setLanguage(meta.lang);
    var ctx = pdf.context;
    ctx.trailerInfo.ID = ctx.obj([L.PDFHexString.of(hex16()), L.PDFHexString.of(hex16())]);
    var pdfa = o.pdfa ? (typeof o.pdfa === 'object' ? o.pdfa : { part: parseInt(o.pdfa, 10) || 3, conformance: (String(o.pdfa).replace(/\d/g, '') || 'b').toUpperCase() }) : null;
    if (pdfa) {
        var icc = ctx.flateStream(b64bytes(SRGB_ICC_B64), { N: 3 });
        var oi = ctx.obj({
            Type: 'OutputIntent', S: 'GTS_PDFA1', OutputConditionIdentifier: L.PDFString.of('sRGB IEC61966-2.1'),
            Info: L.PDFString.of('sRGB IEC61966-2.1'), RegistryName: L.PDFString.of('http://www.color.org'), DestOutputProfile: ctx.register(icc)
        });
        pdf.catalog.set(L.PDFName.of('OutputIntents'), ctx.obj([ctx.register(oi)]));
        var xmp = buildXmp(meta, pdfa.part || 3, pdfa.conformance || 'B', o.xmpExtra);
        var xs = ctx.stream(new TextEncoder().encode(xmp), { Type: 'Metadata', Subtype: 'XML' });
        pdf.catalog.set(L.PDFName.of('Metadata'), ctx.register(xs));
    }
    [].concat(o.attachments || []).forEach(function (a) { addAttachment(pdf, a, L); });
    return pdf.save({ useObjectStreams: true });
}

function fromElement(el, opts) {
    opts = opts || {};
    return render(el, opts).then(function (r) {
        if (opts.as === 'blob') return new Blob([r.bytes], { type: 'application/pdf' });
        return r.bytes;
    });
}
/* ── слайды ──────────────────────────────────────────────────────────────────
   HtmlPdf.slides([слайд, …], opts) → {bytes, pages, warnings, ms}. Слайд — элемент
   фиксированного размера (страница = его коробка); его ДЕТИ — объекты в порядке слоёв:
   каждый меряется в СВОИХ координатах (поворот на время замера снят — getClientRects
   отдаёт прямоугольники, параллельные осям, и у повёрнутого блока они были бы неверны) и
   рисуется группой: матрица transform (вокруг transform-origin), прозрачность, обрезка
   (overflow:hidden со скруглением). Так поворот, порядок слоёв, opacity и кадрирование
   картинок переносятся без отдельного художника. */
function parseMatrix(t) {
    if (!t || t === 'none') return [1, 0, 0, 1, 0, 0];
    var m = /^matrix\(([^)]+)\)$/.exec(t);
    if (m) return m[1].split(',').map(parseFloat);
    var m3 = /^matrix3d\(([^)]+)\)$/.exec(t);
    if (m3) { var v = m3[1].split(',').map(parseFloat); return [v[0], v[1], v[4], v[5], v[12], v[13]]; }
    return [1, 0, 0, 1, 0, 0];
}
function measureSlide(node, o, warn) {
    var items = [];
    Array.prototype.forEach.call(node.children, function (ch) {
        var cs = getComputedStyle(ch);
        if (cs.display === 'none' || cs.visibility === 'hidden' || ch.hasAttribute('data-hp-skip')) return;
        var keep = ch.style.transform;
        if (cs.transform && cs.transform !== 'none') ch.style.transform = 'none';
        var w = ch.offsetWidth, h = ch.offsetHeight, x0 = ch.offsetLeft, y0 = ch.offsetTop;
        if (typeof w !== 'number') {
            /* SVG прямо в слайде (фон темы): у SVGElement нет offset* — без этой ветки фон
               выпадал молча, и тёмная тема уходила в PDF и PNG белой. Коробка — от рамки
               слайда, без своего transform; слайд в масштабе k приводится к своим px. */
            var b = ch.getBoundingClientRect(), nb = node.getBoundingClientRect();
            var k = nb.width > 0 && node.offsetWidth ? node.offsetWidth / nb.width : 1;
            w = b.width * k; h = b.height * k;
            x0 = (b.left - nb.left) * k - node.clientLeft; y0 = (b.top - nb.top) * k - node.clientTop;
        }
        if (!(w > 0 && h > 0)) { ch.style.transform = keep; return; }
        var mtx = parseMatrix(cs.transform);
        var org = String(cs.transformOrigin || '').split(/\s+/).map(px);
        var M;
        try { M = measure(ch, o, warn); } finally { ch.style.transform = keep; }
        M.ops = M.ops.filter(function (op) { return op.t !== 'link'; });
        var clip = cs.overflow === 'hidden' || cs.overflowX === 'hidden' || cs.overflow === 'clip';
        items.push({
            M: M, L: x0, T: y0, w: w, h: h, m: mtx, ox: org[0] || w / 2, oy: org[1] || h / 2,
            opacity: cs.opacity === '' ? 1 : +cs.opacity,
            clip: clip ? [radiusOf(cs.borderTopLeftRadius), radiusOf(cs.borderTopRightRadius), radiusOf(cs.borderBottomRightRadius), radiusOf(cs.borderBottomLeftRadius)] : null
        });
    });
    return { W: node.offsetWidth, H: node.offsetHeight, items: items };
}
function slides(nodes, opts) {
    var o = opts || {}, t0 = Date.now(), warnings = [];
    function warn(code) { if (warnings.indexOf(code) < 0) warnings.push(code); }
    function prog(f, phase) { try { if (o.onProgress) o.onProgress(f, phase); } catch (e) {} }
    var list = Array.prototype.slice.call(nodes || []);
    var sig = o.signal;
    return load().then(function () {
        checkAbort(sig); prog(0.1, 'libs');
        var fr = document.fonts && document.fonts.ready ? bounded(document.fonts.ready, o.fontsWaitMs || 6000, 'fonts_ready').catch(function () {}) : null;
        var imgs = [];
        list.forEach(function (n) { imgs = imgs.concat(Array.prototype.slice.call(n.querySelectorAll('img'))); });
        return Promise.all([fr].concat(imgs.map(function (im) { return im.decode ? bounded(im.decode(), 10000, 'img_decode').catch(function () {}) : null; })));
    }).then(function () {
        checkAbort(sig);
        var S = list.map(function (n) { return measureSlide(n, o, warn); });
        prog(0.3, 'measure');
        return buildSlides(S, o, warn, sig, prog);
    }).then(function (res) {
        prog(1, 'done');
        return { bytes: res.bytes, pages: res.pages, warnings: warnings, ms: Date.now() - t0 };
    });
}
function buildSlides(S, o, warn, sig, prog) {
    var L = root.PDFLib, P = root.PdfTextFont;
    return L.PDFDocument.create({ updateMetadata: false }).then(function (pdf) {
        pdf.registerFontkit(root.fontkit);
        pdf.__fontkitRegistered = true;
        var pages = S.map(function (s) { return pdf.addPage([s.W * PT, s.H * PT]); });
        var metric = {}, needMetric = fontNeeder(pdf, metric), textOps = [], imgOps = [];
        S.forEach(function (s) {
            s.items.forEach(function (it) {
                it.PG = { srcX0: 0, srcY0: 0, srcX1: it.w, srcY1: it.h, dstY0: 0, wPx: it.w, hPx: it.h, clip: [0, 0, it.w, it.h], twoD: true };
                it.M.ops.forEach(function (op) {
                    if (op.t === 'text' || op.t === 'marker') { planFaces(op, needMetric); textOps.push(op); }
                    else if (op.t === 'img') imgOps.push(op);
                });
            });
        });
        var fonts = {};
        return Promise.all(Object.keys(metric).map(function (k) { return metric[k].then(function (f) { fonts[k] = f; }); })).then(function () {
            checkAbort(sig); prog(0.45, 'fonts');
            var fbNeed = {};
            textOps.forEach(function (op) { op.pieces = op.t === 'marker' && op.mk.shape ? [] : planPieces(op, fonts, fbNeed, o.lang || ''); });
            return loadFallbacks(pdf, P, fbNeed, warn);
        }).then(function (fb) {
            checkAbort(sig); prog(0.6, 'fallback');
            var jobs = [];
            textOps.forEach(function (op) {
                op.pieces.forEach(function (pc) {
                    if (pc.kind === 'fb') { pc.font = fb[pc.fbKey] || null; if (!pc.font) pc.kind = 'img'; }
                    if (pc.kind === 'img') jobs.push(glyphImage(pdf, pc, op).then(function (im) { pc.img = im; }));
                });
            });
            imgOps.forEach(function (op) { jobs.push(embedImage(pdf, op, o, warn).then(function (im) { op.pdfImg = im; })); });
            return Promise.all(jobs);
        }).then(function () {
            checkAbort(sig); prog(0.75, 'images');
            var rtlFonts = new Set();
            S.forEach(function (s, si) {
                var pg = pages[si], Hpt = s.H * PT;
                s.items.forEach(function (it) {
                    var a = it.m[0], b = it.m[1], c = it.m[2], d = it.m[3], e = it.m[4], f = it.m[5];
                    var ox = it.ox, oy = it.oy;
                    /* Местная система рисования — x вправо, y вверх от нижнего левого угла
                       коробки (pt). Точка коробки p (px, y вниз) уходит на слайд как
                       L,T + o + M·(p − o) + (e,f); в pt с y вверх это cm = [a, −b, −c, d, E, F]. */
                    var E = PT * (it.L + ox - a * ox + c * (it.h - oy) + e);
                    var F = Hpt - PT * (it.T + oy - b * ox + d * (it.h - oy) + f);
                    var pre = [L.pushGraphicsState(), L.concatTransformationMatrix(a, -b, -c, d, E, F)];
                    if (it.opacity < 0.999) pre.push(L.setGraphicsState(pg.maybeEmbedGraphicsState({ opacity: it.opacity, borderOpacity: it.opacity })));
                    pg.pushOperators.apply(pg, pre);
                    if (it.clip) {
                        var K = 0.5523, w = it.w * PT, h = it.h * PT, rad = it.clip.map(function (v) { return Math.min(v * PT, w / 2, h / 2); });
                        var tl = rad[0], tr = rad[1], br = rad[2], bl = rad[3];
                        var cp = (tl || tr || br || bl) ? [
                            L.moveTo(tl, h), L.lineTo(w - tr, h), L.appendBezierCurve(w - tr + tr * K, h, w, h - tr + tr * K, w, h - tr),
                            L.lineTo(w, br), L.appendBezierCurve(w, br - br * K, w - br + br * K, 0, w - br, 0),
                            L.lineTo(bl, 0), L.appendBezierCurve(bl - bl * K, 0, 0, bl - bl * K, 0, bl),
                            L.lineTo(0, h - tl), L.appendBezierCurve(0, h - tl + tl * K, tl - tl * K, h, tl, h), L.closePath()
                        ] : [L.rectangle(0, 0, w, h)];
                        pg.pushOperators.apply(pg, cp.concat([L.clip(), L.endPath()]));
                    }
                    var PG = [it.PG];
                    var X = function (k, x) { return x * PT; }, Y = function (k, y) { return PG[0].hPx * PT - y * PT; };
                    draw(pdf, [pg], PG, it.M, { pages: PG }, o, [], X, Y, L);
                    pg.pushOperators(L.popGraphicsState());
                });
            });
            textOps.forEach(function (op) { op.pieces.forEach(function (pc) { if (pc.rtl && pc.font) rtlFonts.add(pc.font); }); });
            rtlLigatureText(rtlFonts);
            prog(0.9, 'draw');
            return finish(pdf, o, L);
        }).then(function (bytes) { return { bytes: bytes, pages: S.length }; });
    });
}

/* Вложить файл в готовый PDF (Factur-X: name 'factur-x.xml', mime 'text/xml', afRelationship 'Data'
   или 'Alternative'). Метаданные (Info, XMP, /ID) остаются прежними — PDF/A не ломается. */
function attach(pdfBytes, a) {
    return load().then(function () {
        var L = root.PDFLib;
        return L.PDFDocument.load(pdfBytes, { updateMetadata: false }).then(function (pdf) {
            [].concat(a || []).forEach(function (x) { addAttachment(pdf, x, L); });
            return pdf.save({ useObjectStreams: true });
        });
    });
}

return {
    VERSION: VERSION, config: config, load: load, fromElement: fromElement, render: render, attach: attach,
    slides: slides, registerFamily: registerFamily,
    fontCss: fontCss, installFonts: installFonts, FAMILY: FAMILY,
    xmp: { facturX: facturXXmp }, _pure: PURE
};
});
